import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import FollowupControls from '@/components/admin/FollowupControls';
import { fmtDateTime, fmtDay, fmtDate } from '@/utils/dateFmt';
import { fmtNum } from '@/utils/numFmt';
import { toast } from 'sonner';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { CaretLeft, Plus, Trash, PencilSimple, FloppyDisk, Camera, UploadSimple, CalendarCheck, ArrowsDownUp, CaretUp, CaretDown, FilePdf, Heart, ChatCircle, Eye, EyeSlash, Receipt, Warning, PaperPlaneTilt, ClockCounterClockwise, TestTube, FileText, Copy } from '@phosphor-icons/react';
import { LineChart, Line, BarChart, Bar, Cell, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, CartesianGrid, Legend, ReferenceLine } from 'recharts';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import BabyMenuTab from '@/components/BabyMenuTab';
import { PlanDetail as PlanDetailComponent } from '@/pages/PlanosPage';
import AtalahChart from '@/components/AtalahChart';
import { ProtocolChecklist, ProtocolHistory } from '@/components/ProtocolWidget';
import { Md } from '@/components/Md';
import InBodyBackoffice from '@/components/InBodyBackoffice';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';

const API = process.env.REACT_APP_BACKEND_URL;

const LAB_EXAMS_COL1 = ["Hemograma Completo", "Glicose em Jejum", "TTOG", "HbA1c", "Frutosamina", "Insulina", "Uréia", "Creatinina", "Ácido Úrico", "Triglicerídeos", "LDL-C", "HDL-C", "Colesterol Total", "Colesterol NÃO HDL-C", "TGO/AST", "Cálcio", "Cobre", "CK"];
const LAB_EXAMS_COL2 = ["TGP/ALT", "Bilirrubina Total e Frações", "Fosfatase Alcalina", "GGT", "Ferro", "Ferritina", "Transferrina", "PCR", "Vitamina B12", "Vitamina B9", "Homocisteína", "Vitamina A", "Vitamina D", "Vitamina E", "Vitamina K", "Magnésio", "Potássio", "Fósforo", "TSH", "T3 e T4 Livre", "Anti-TPO", "Sódio", "Zinco", "Albumina"];
const labSlug = (s) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

// Import shared patient type helpers (normalize + age-based detection)
import { isBabyPatient, isGravidaPatient, isPosPartoPatient } from '@/lib/utils';
import RecipesTab from '@/components/admin/RecipesTab';
import PerimetrosTab from '@/components/PerimetrosTab';
import NotificationPreferencesPanel from '@/components/NotificationPreferencesPanel';
import { promptEnableReminders } from '@/lib/remindersPrompt';
// PROMPT 5 — Item 1: tabs extraídos do ficheiro (zero alteração de comportamento)
import PagamentosPatientTab from '@/components/admin/patient-tabs/PagamentosPatientTab';
import MetasAdminTab from '@/components/admin/patient-tabs/MetasAdminTab';
// PROMPT 6 — Item 1: extracção dos 6 tabs restantes
import AnamneseTab from '@/components/admin/patient-tabs/AnamneseTab';
import ConsultasTab from '@/components/admin/patient-tabs/ConsultasTab';
import DiárioTab from '@/components/admin/patient-tabs/DiarioAdminTab';
import AvaliacaoTab from '@/components/admin/patient-tabs/AvaliacaoTab';
import PlanosTab from '@/components/admin/patient-tabs/PlanosTab';
import NecessidadesTab from '@/components/admin/patient-tabs/NecessidadesTab';
import PassosTab from '@/components/admin/patient-tabs/PassosTab';
import GlicemiaTab from '@/components/admin/patient-tabs/GlicemiaTab';
import HidratacaoTab from '@/components/admin/patient-tabs/HidratacaoTab';
import CountryCodeSelect from '@/components/CountryCodeSelect';
import { WhatsAppConfirmButton } from '@/components/admin/WhatsAppConfirmButton';
import { useConfirmDelete, useConfirmClear } from '@/components/ConfirmDialogs';
import { FieldVersionHistoryModal } from '@/components/FieldVersionHistoryModal';
import { ProgramInstallments, ProgramSummary } from '@/components/admin/ProgramInstallments';
import { fmtDate as fmtDatePt } from '@/utils/dateFmt';

// ─── PROGRAM PHASE FIELD (dropdown with auto-suggestion) ───
function ProgramPhaseField({ patient, onRefresh }) {
  const [phases, setPhases] = useState({});
  const [suggested, setSuggested] = useState('');
  const pType = patient.patient_type || '';
  const showField = pType === 'gravida' || pType === 'introducao_alimentar';
  const pid = patient.user_id;

  useEffect(() => {
    if (!showField) return;
    let cancelled = false;
    (async () => {
      try {
        const [p, s] = await Promise.all([
          axios.get(`${API}/api/program-phases`),
          axios.get(`${API}/api/patients/${pid}/program-phase/suggest`),
        ]);
        if (cancelled) return;
        setPhases(p.data || {});
        setSuggested(s.data?.suggested || '');
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [pid, showField]);

  if (!showField) return null;

  const options = phases[pType] || [];
  const current = patient.program_phase || '';

  const save = async (val) => {
    try {
      await axios.put(`${API}/api/patients/${patient.user_id}`, { program_phase: val });
      onRefresh();
    } catch { toast.error('Erro'); }
  };

  return (
    <div data-testid="program-phase-field">
      <Label className="text-xs text-brand-muted mb-1 block">Fase do programa</Label>
      <select
        value={current}
        onChange={e => save(e.target.value)}
        className="w-full border border-brand-border rounded-xl px-3 py-2 text-sm bg-white"
        data-testid="program-phase-select"
      >
        <option value="">Selecionar fase…</option>
        {options.map(o => (
          <option key={o.key} value={o.key}>{o.label}</option>
        ))}
      </select>
      {suggested && suggested !== current && (
        <button
          type="button"
          onClick={() => save(suggested)}
          className="text-[10px] text-brand-green hover:underline mt-1"
          data-testid="apply-suggested-phase"
        >
          Aplicar sugestão: {options.find(o => o.key === suggested)?.label || suggested}
        </button>
      )}
    </div>
  );
}

// ─── RESUMO TAB (agora dividido em secções: resumo/consultas/clinico/documentos/dados) ───
function ResumoTab({ patient, onRefresh, section, onNavigateTab }) {
  const confirmDelete = useConfirmDelete();
  const confirmClear = useConfirmClear();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [appointments, setAppointments] = useState([]);
  const [appointmentHistory, setAppointmentHistory] = useState([]);
  const [showApptHistory, setShowApptHistory] = useState(false);
  const [supplements, setSupplements] = useState([]);
  const [checkinDay, setCheckinDay] = useState(null);
  const [showAptForm, setShowAptForm] = useState(false);
  const [aptForm, setAptForm] = useState({ date: '', time: '', type: 'presencial', location: '', link: '', notes: '' });
  const [showSuppForm, setShowSuppForm] = useState(false);
  const [suppItems, setSuppItems] = useState([]);
  const [suppLibrary, setSuppLibrary] = useState([]);
  const [showSuppHistory, setShowSuppHistory] = useState(false);
  const [suppHistory, setSuppHistory] = useState([]);
  const [showLabForm, setShowLabForm] = useState(false);
  const [labDoctor, setLabDoctor] = useState('');
  const [labExams, setLabExams] = useState({});
  const [labOutrosChecked, setLabOutrosChecked] = useState(false);
  const [labOutrosText, setLabOutrosText] = useState('');
  const [labNotes, setLabNotes] = useState('');
  const [labNotesPaciente, setLabNotesPaciente] = useState('');
  const [generatingLabPdf, setGeneratingLabPdf] = useState(false);
  const [showLabHistory, setShowLabHistory] = useState(false);
  const [labHistory, setLabHistory] = useState([]);
  const [showResetPw, setShowResetPw] = useState(false);
  const [resendingAccess, setResendingAccess] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [locations, setLocations] = useState([]);
  const [patientTags, setPatientTags] = useState([]);
  const [notesDraft, setNotesDraft] = useState(patient.notes || '');
  const [savingNotes, setSavingNotes] = useState(false);
  const [showNotesHistory, setShowNotesHistory] = useState(false);
  const [tpDraft, setTpDraft] = useState(patient.therapeutic_purpose || '');
  const [tpEditing, setTpEditing] = useState(false);
  const [tpSaving, setTpSaving] = useState(false);
  const [showTpHistory, setShowTpHistory] = useState(false);
  const [lastCheckin, setLastCheckin] = useState(null);
  const [lastDiaryEntries, setLastDiaryEntries] = useState([]);
  const pid = patient.user_id;
  const { user } = useAuth();
  const cedulaMissing = !((user?.license_number || '').trim());
  const [declTemplates, setDeclTemplates] = useState([]);
  const [declConsultations, setDeclConsultations] = useState([]);
  const [showDeclForm, setShowDeclForm] = useState(false);
  const [declForm, setDeclForm] = useState({ template_id: '', language: 'pt', consultation_id: '', nome_acompanhante: '', escola: '', conteudo: '', hora_inicio: '', hora_fim: '', data_consulta: '', final_text: '', share_with_patient: false });
  const [previewingDecl, setPreviewingDecl] = useState(false);
  const [generatingDeclPdf, setGeneratingDeclPdf] = useState(false);
  const [showDeclHistory, setShowDeclHistory] = useState(false);
  const [declHistory, setDeclHistory] = useState([]);
  const [declLocationWarning, setDeclLocationWarning] = useState(false);

  useEffect(() => { 
    const existingTypes = patient.patient_types || [];
    const primaryType = patient.patient_type || '';
    // Don't auto-add 'geral' if patient already has a real type
    const types = existingTypes.length > 0 ? existingTypes : (primaryType && primaryType !== 'geral' ? [primaryType] : []);
    setForm({ name: patient.name || '', email: patient.email || '', sex: patient.sex || '', phone: patient.phone || '', country_code: patient.country_code || '+351', client_id: patient.client_id || '', birth_date: patient.birth_date || '', patient_type: primaryType || 'geral', patient_types: types, notes: patient.notes || '', dum: patient.dum || '', delivery_date: patient.delivery_date || '', preferred_name: patient.preferred_name || '', citizen_id: patient.citizen_id || '', nif: patient.nif || '', sns_number: patient.sns_number || '', street: patient.street || '', postal_code: patient.postal_code || '', city: patient.city || '', country: patient.country || 'Portugal', occupation: patient.occupation || '', process_number: patient.process_number || '', consultation_location: patient.consultation_location || '', insurer_name: patient.insurer_name || '', insurer_policy: patient.insurer_policy || '', guardian_name: patient.guardian_name || '', guardian_emails: (patient.guardian_emails || []).join(', ') }); 
  }, [patient]);
  useEffect(() => {
    (async () => {
      try { const { data } = await axios.get(`${API}/api/patients/${pid}/appointments`); setAppointments(data); } catch {}
      try { const { data } = await axios.get(`${API}/api/patients/${pid}/appointments-history`); setAppointmentHistory(data || []); } catch {}
      try { const { data } = await axios.get(`${API}/api/config/consultation-locations`); setLocations(data); } catch {}
      try { const { data } = await axios.get(`${API}/api/patient-tags`); setPatientTags(data?.tags || []); } catch {}
      try { const { data } = await axios.get(`${API}/api/patients/${pid}/supplements`); setSuppItems(data.items || []); setSupplements(data.items || []); } catch {}
      try { const { data } = await axios.get(`${API}/api/admin/supplement-library`); setSuppLibrary(data || []); } catch {}
      try { const { data } = await axios.get(`${API}/api/patients/${pid}/checkins`); const sorted = [...(data || [])].sort((a, b) => (b.date || '').localeCompare(a.date || '')); setLastCheckin(sorted[0] || null); } catch {}
      try { const { data } = await axios.get(`${API}/api/patients/${pid}/diary`); const sorted = [...(data || [])].sort((a, b) => (b.date || '').localeCompare(a.date || '')); setLastDiaryEntries(sorted.slice(0, 2)); } catch {}
    })();
  }, [pid]);

  useEffect(() => { setNotesDraft(patient.notes || ''); }, [patient.notes]);
  useEffect(() => { if (!tpEditing) setTpDraft(patient.therapeutic_purpose || ''); }, [patient.therapeutic_purpose, tpEditing]);

  const save = async () => {
    const was = (patient.notes || '').trim();
    const now = (form.notes || '').trim();
    if (was && !now) {
      const ok = await confirmClear('Observações');
      if (!ok) return;
    }
    try { await axios.put(`${API}/api/patients/${pid}`, form); toast.success('Dados atualizados'); setEditing(false); onRefresh(); } catch { toast.error('Erro ao guardar'); }
  };
  const saveNotes = async () => {
    const was = (patient.notes || '').trim();
    const now = (notesDraft || '').trim();
    if (was && !now) {
      const ok = await confirmClear('Observações');
      if (!ok) return;
    }
    setSavingNotes(true);
    try { await axios.put(`${API}/api/patients/${pid}`, { notes: notesDraft }); toast.success('Observações guardadas'); onRefresh(); } catch { toast.error('Erro ao guardar'); }
    finally { setSavingNotes(false); }
  };
  const saveApt = async () => {
    try { const { data } = await axios.post(`${API}/api/patients/${pid}/appointments`, aptForm); setAppointments([...appointments, data].sort((a, b) => a.date.localeCompare(b.date))); setShowAptForm(false); setAptForm({ date: '', time: '', type: 'presencial' }); toast.success('Consulta agendada'); } catch { toast.error('Erro'); }
  };
  const deleteApt = async (id) => {
    const apt = appointments.find(a => a.id === id);
    const ok = await confirmDelete({ description: `Vai apagar a marcação de ${fmtDateTime(apt?.date, apt?.time)}.` });
    if (!ok) return;
    try { await axios.delete(`${API}/api/appointments/${id}`); setAppointments(appointments.filter(a => a.id !== id)); toast.success('Consulta eliminada'); } catch {}
  };
  const saveSupps = async () => {
    try { await axios.put(`${API}/api/patients/${pid}/supplements`, { items: suppItems }); setSupplements(suppItems); toast.success('Suplementação guardada'); setShowSuppForm(false); promptEnableReminders(pid, 'supplements'); } catch { toast.error('Erro'); }
  };
  const openSuppHistory = async () => {
    setShowSuppHistory(true);
    try { const { data } = await axios.get(`${API}/api/patients/${pid}/supplementation/history`); setSuppHistory(data || []); } catch { setSuppHistory([]); }
  };
  const toggleLabExam = (name) => setLabExams(prev => ({ ...prev, [name]: !prev[name] }));
  const downloadBlobFile = (data, filename) => {
    const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };
  const generateLabPdf = async () => {
    setGeneratingLabPdf(true);
    try {
      const selected_exams = Object.keys(labExams).filter(k => labExams[k]);
      if (labOutrosChecked && !selected_exams.includes('Outros')) selected_exams.push('Outros');
      const { data } = await axios.post(`${API}/api/patients/${pid}/lab-request/pdf`, {
        doctor_name: labDoctor, selected_exams, outros_text: labOutrosText, notes: labNotes, notes_paciente: labNotesPaciente,
      }, { responseType: 'blob' });
      downloadBlobFile(data, `pedido_exames_${(patient.name || 'paciente').replace(/\s+/g, '_').toLowerCase()}.pdf`);
      toast.success('PDF gerado');
      setShowLabForm(false);
    } catch (err) {
      toast.error('Erro ao gerar PDF');
    } finally {
      setGeneratingLabPdf(false);
    }
  };
  const openLabHistory = async () => {
    setShowLabHistory(true);
    try { const { data } = await axios.get(`${API}/api/patients/${pid}/lab-requests`); setLabHistory(Array.isArray(data) ? data : []); } catch { setLabHistory([]); }
  };
  const redownloadLabRequest = async (req) => {
    try {
      const { data } = await axios.get(`${API}/api/lab-requests/${req.id}/pdf`, { responseType: 'blob' });
      downloadBlobFile(data, `pedido_exames_${req.created_at.slice(0, 10)}.pdf`);
    } catch { toast.error('Erro ao descarregar PDF'); }
  };
  const declEndTime = (t, durationMin = 60) => {
    if (!t) return '';
    const [h, m] = t.split(':').map(Number);
    if (Number.isNaN(h)) return '';
    const total = h * 60 + (m || 0) + durationMin;
    const eh = Math.floor(total / 60) % 24, em = total % 60;
    return `${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}`;
  };
  const calcAgeYears = (birthDate) => {
    if (!birthDate) return null;
    const bd = new Date(birthDate); const today = new Date();
    if (Number.isNaN(bd.getTime())) return null;
    let age = today.getFullYear() - bd.getFullYear();
    if (today.getMonth() < bd.getMonth() || (today.getMonth() === bd.getMonth() && today.getDate() < bd.getDate())) age--;
    return age;
  };
  const declExtractTags = (tpl) => {
    const src = `${tpl?.text_pt || ''} ${tpl?.text_en || ''}`;
    const set = new Set();
    (src.match(/\{([a-z_]+)\}/g) || []).forEach(m => set.add(m.slice(1, -1)));
    return set;
  };
  const declSelectedTemplate = declTemplates.find(t => t.id === declForm.template_id) || null;
  const declTags = declSelectedTemplate ? declExtractTags(declSelectedTemplate) : new Set();
  const declNeedsConsult = declTags.has('data_consulta') || declTags.has('hora_inicio') || declTags.has('hora_fim') || declTags.has('local_consulta') || declTags.has('local');
  const declNeedsAcompanhante = declTags.has('nome_acompanhante') || declTags.has('nome_encarregado');
  const declMissingFieldMsg = () => {
    if (!declForm.template_id) return 'Escolhe um modelo';
    if (declNeedsConsult && !declForm.consultation_id) return 'Escolhe a consulta';
    if (declNeedsAcompanhante && !declForm.nome_acompanhante.trim()) return 'Preenche o nome do acompanhante';
    if (declTags.has('escola') && !declForm.escola.trim()) return 'Preenche a escola/creche';
    if (declTags.has('conteudo') && !declForm.conteudo.trim()) return 'Preenche o conteúdo';
    if (!declForm.final_text.trim()) return 'Pré-visualiza o texto primeiro';
    return '';
  };
  const openDeclForm = async () => {
    const age = calcAgeYears(patient.birth_date);
    const isMinor = age !== null && age < 18;
    setDeclForm({ template_id: '', language: 'pt', consultation_id: '', nome_acompanhante: isMinor ? (patient.guardian_name || '') : '', escola: '', conteudo: '', hora_inicio: '', hora_fim: '', data_consulta: '', final_text: '', share_with_patient: false });
    setDeclLocationWarning(false);
    setShowDeclForm(true);
    if (declTemplates.length === 0) { try { const { data } = await axios.get(`${API}/api/declaration-templates`); setDeclTemplates(data || []); } catch {} }
    try { const { data } = await axios.get(`${API}/api/patients/${pid}/consultations/for-declaration`); setDeclConsultations(data || []); } catch {}
  };
  const onPickDeclConsultation = (cid) => {
    const c = declConsultations.find(x => x.id === cid);
    if (!c) { setDeclForm(prev => ({ ...prev, consultation_id: '' })); return; }
    let hora_inicio = '', hora_fim = '';
    if (c.appointment_time) { hora_inicio = c.appointment_time.slice(0, 5); hora_fim = declEndTime(hora_inicio, c.duration_min || 60); }
    setDeclForm(prev => ({ ...prev, consultation_id: cid, data_consulta: c.date, hora_inicio, hora_fim, final_text: '' }));
    setDeclLocationWarning(false);
  };
  const previewDecl = async () => {
    if (!declForm.template_id) { toast.error('Escolhe um modelo'); return; }
    setPreviewingDecl(true);
    try {
      const { data } = await axios.post(`${API}/api/patients/${pid}/declaration/preview`, declForm);
      setDeclForm(prev => ({ ...prev, final_text: data.final_text }));
      setDeclLocationWarning(!!data.location_warning);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao pré-visualizar'); }
    finally { setPreviewingDecl(false); }
  };
  const generateDeclPdf = async () => {
    if (!declForm.final_text.trim()) { toast.error('Pré-visualiza o texto primeiro'); return; }
    setGeneratingDeclPdf(true);
    try {
      const { data } = await axios.post(`${API}/api/patients/${pid}/declaration/pdf`, declForm, { responseType: 'blob' });
      downloadBlobFile(data, `declaracao_${(patient.name || 'paciente').replace(/\s+/g, '_').toLowerCase()}.pdf`);
      toast.success('Declaração gerada');
      setShowDeclForm(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao gerar PDF'); }
    finally { setGeneratingDeclPdf(false); }
  };
  const openDeclHistory = async () => {
    setShowDeclHistory(true);
    try { const { data } = await axios.get(`${API}/api/patients/${pid}/declarations`); setDeclHistory(Array.isArray(data) ? data : []); } catch { setDeclHistory([]); }
  };
  const redownloadDecl = async (h) => {
    try {
      const { data } = await axios.get(`${API}/api/declarations/${h.id}/pdf`, { responseType: 'blob' });
      downloadBlobFile(data, `declaracao_${h.created_at.slice(0, 10)}.pdf`);
    } catch { toast.error('Erro ao descarregar PDF'); }
  };
  const duplicateDecl = async (h) => {
    if (declTemplates.length === 0) { try { const { data } = await axios.get(`${API}/api/declaration-templates`); setDeclTemplates(data || []); } catch {} }
    try { const { data } = await axios.get(`${API}/api/patients/${pid}/consultations/for-declaration`); setDeclConsultations(data || []); } catch {}
    setDeclForm({
      template_id: h.template_id, language: h.language || 'pt', consultation_id: h.consultation_id || '',
      nome_acompanhante: h.nome_acompanhante || '', escola: h.escola || '', conteudo: h.conteudo || '',
      hora_inicio: h.hora_inicio || '', hora_fim: h.hora_fim || '', data_consulta: h.data_consulta || '',
      final_text: h.final_text || '', share_with_patient: false,
    });
    setDeclLocationWarning(false);
    setShowDeclHistory(false);
    setShowDeclForm(true);
  };
  const saveCheckinDay = async (day) => {
    try { await axios.put(`${API}/api/patients/${pid}/checkin-day`, { weekday: day }); setCheckinDay(day); toast.success('Dia de check-in definido'); } catch { toast.error('Erro'); }
  };
  const dayNames = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

  const renderField = ({ label, field, type }) => (
    <div>
      <Label className="text-xs text-brand-muted mb-1 block">{label}</Label>
      {editing ? (
        type === 'select' ? (
          <div>
            <div className="flex flex-wrap gap-1.5">
              {patientTags.map(t => {
                const selected = (form.patient_types || [form[field]]).includes(t.value);
                return <button key={t.value} type="button" onClick={() => {
                  const current = form.patient_types || (form[field] ? [form[field]] : []);
                  const updated = selected ? current.filter(v => v !== t.value) : [...current, t.value];
                  const final = updated.length ? updated : [];
                  setForm({ ...form, patient_types: final, [field]: final[0] || '' });
                }} className={`px-2.5 py-1 rounded-full text-[11px] font-medium ${selected ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`} data-testid={`tag-${t.value}`}>{t.label}</button>;
              })}
            </div>
            <p className="text-[9px] text-brand-placeholder mt-1">Podes selecionar várias categorias</p>
          </div>
        ) : type === 'textarea' ? (
          <Textarea value={form[field] || ''} onChange={e => setForm({ ...form, [field]: e.target.value })} className="rounded-xl border-brand-border text-sm" rows={3} data-testid={`edit-${field}`} />
        ) : (
          <Input type={type || 'text'} value={form[field] || ''} onChange={e => setForm({ ...form, [field]: e.target.value })} className="rounded-xl border-brand-border text-sm" data-testid={`edit-${field}`} />
        )
      ) : (
        <p className="text-sm text-brand-text">{type === 'date' ? (patient[field] ? fmtDatePt(patient[field]) : '—') : type === 'select' ? ((patient.patient_types || [patient[field]]).filter(Boolean).map(v => patientTags.find(t => t.value === v)?.label || v).join(', ') || '-') : (Array.isArray(patient[field]) ? (patient[field].join(', ') || '-') : (patient[field] || '-'))}</p>
      )}
    </div>
  );

  return (
    <div className="space-y-5" data-testid={`tab-section-${section}`}>
      {section === 'resumo' && (<>
        {/* Protocol widget */}
        <ProtocolChecklist patientId={patient?.user_id} />
        {/* Observações — bloco próprio, grava independentemente dos Dados da paciente */}
        <div className="bg-white border border-brand-border rounded-2xl p-4" data-testid="observacoes-card">
          <div className="flex justify-between items-center mb-2">
            <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider">Observações</h3>
            <div className="flex items-center gap-3">
              {(patient.notes_history || []).length > 0 && (
                <button onClick={() => setShowNotesHistory(true)} className="text-xs text-brand-green hover:underline" data-testid="history-btn-observacoes-ficha">Ver versões anteriores</button>
              )}
              <button onClick={saveNotes} disabled={savingNotes} className="text-xs text-brand-green font-medium disabled:opacity-50" data-testid="save-observacoes-btn">{savingNotes ? 'A guardar...' : 'Guardar'}</button>
            </div>
          </div>
          <Textarea value={notesDraft} onChange={e => setNotesDraft(e.target.value)} className="rounded-xl border-brand-border text-sm" rows={3} data-testid="observacoes-textarea" />
          <FieldVersionHistoryModal
            open={showNotesHistory}
            onOpenChange={setShowNotesHistory}
            fieldLabel="Observações"
            history={patient.notes_history || []}
            onRestore={(text) => setNotesDraft(text)}
          />
        </div>
        {/* Notas da cliente (só leitura) — logo a seguir às Observações */}
        {patient.client_notes && (
          <div className="bg-brand-bg/50 rounded-2xl p-4" data-testid="client-notes-card">
            <p className="text-[10px] text-brand-muted uppercase tracking-wider mb-1">Notas da cliente</p>
            <p className="text-xs text-brand-text whitespace-pre-line">{patient.client_notes}</p>
          </div>
        )}
        <SymptomsSection patientId={patient.user_id} />
        {/* Suplementação atual — resumo só leitura; edição completa fica em Clínico */}
        <div className="bg-white border border-brand-border rounded-2xl p-4" data-testid="suplementacao-preview-card">
          <div className="flex justify-between mb-2">
            <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider">Suplementação atual</h3>
            <button onClick={() => onNavigateTab?.('clinico')} className="text-[10px] text-brand-green hover:underline">Ver em Clínico</button>
          </div>
          {supplements.length === 0 ? (
            <p className="text-xs text-brand-placeholder">Sem suplementação definida.</p>
          ) : (
            <div className="space-y-1.5">
              {supplements.map((s, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-brand-bg/50 rounded-xl">
                  <p className="text-sm text-brand-text">{s.name}<span className="text-[10px] text-brand-muted">{s.dose ? ` · ${s.dose}` : ''}</span></p>
                  {s.time && <span className="text-xs text-brand-muted">{s.time}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Último check-in e últimos registos do diário */}
        <div className="bg-white border border-brand-border rounded-2xl p-4" data-testid="last-activity-card">
          <div className="flex justify-between mb-2">
            <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider">Último check-in e diário</h3>
            <button onClick={() => onNavigateTab?.('acompanhamento')} className="text-[10px] text-brand-green hover:underline">Ver em Acompanhamento</button>
          </div>
          {lastCheckin ? (
            <p className="text-xs text-brand-text mb-2">Check-in {fmtDatePt(lastCheckin.date)}: cumprimento {lastCheckin.cumprimento}/5 · energia {lastCheckin.energia}/5 · fome {lastCheckin.fome}/5{lastCheckin.sintomas ? ` · ${lastCheckin.sintomas}` : ''}</p>
          ) : (
            <p className="text-xs text-brand-placeholder mb-2">Sem check-ins registados.</p>
          )}
          {lastDiaryEntries.length === 0 ? (
            <p className="text-xs text-brand-placeholder">Sem registos no diário.</p>
          ) : (
            <div className="space-y-1">
              {lastDiaryEntries.map(e => (
                <p key={e.id} className="text-xs text-brand-text"><span className="text-brand-muted">{fmtDatePt(e.date)} · {e.meal_type}:</span> {e.description}</p>
              ))}
            </div>
          )}
        </div>
      </>)}

      {section === 'dados' && (<>
      <div className="bg-white border border-brand-border rounded-2xl p-4">
        <div className="flex justify-between mb-3">
          <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider">Dados da paciente</h3>
          {editing ? (
            <div className="flex gap-2">
              <button onClick={() => setEditing(false)} className="text-xs text-brand-muted">Cancelar</button>
              <button onClick={save} className="bg-brand-green text-white rounded-full px-3 py-1 text-xs font-medium" data-testid="save-patient-btn">Guardar</button>
            </div>
          ) : (
            <button onClick={() => setEditing(true)} className="text-xs text-brand-green flex items-center gap-1" data-testid="edit-patient-btn"><PencilSimple size={12} /> Editar</button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {renderField({ label: "Nome", field: "name" })}
          {renderField({ label: "Email", field: "email" })}
          <div>
            <Label className="text-xs text-brand-muted mb-1 block">Telefone</Label>
            {editing ? (
              <div className="flex gap-1">
                <CountryCodeSelect value={form.country_code || '+351'} onChange={v => setForm({...form, country_code: v})} testId="patient-country-code" />
                <Input value={form.phone || ''} onChange={e => setForm({...form, phone: e.target.value})} className="flex-1 rounded-xl border-brand-border text-sm" placeholder="912345678" />
              </div>
            ) : (
              <div>
                <p className="text-sm text-brand-text">{patient.country_code || '+351'} {patient.phone || '-'}</p>
                {patient.whatsapp_url && <WhatsAppConfirmButton url={patient.whatsapp_url} className="mt-2" testId="patient-whatsapp-btn" />}
              </div>
            )}
          </div>
          {renderField({ label: "ID Cliente", field: "client_id" })}
          {renderField({ label: "Data de nascimento", field: "birth_date", type: "date" })}
          {renderField({ label: "Tipo de paciente", field: "patient_type", type: "select" })}
          <ProgramPhaseField patient={patient} onRefresh={onRefresh} />
          {renderField({ label: "DUM (Data Última Menstruação)", field: "dum", type: "date" })}
          {renderField({ label: "Data de parto real", field: "delivery_date", type: "date" })}
          {renderField({ label: "Nome preferido", field: "preferred_name" })}
          <div><Label className="text-xs text-brand-muted mb-1 block">Sexo</Label>
            {editing ? (
              <select value={form.sex || ''} onChange={e => setForm({ ...form, sex: e.target.value })} className="w-full border border-brand-border rounded-xl px-3 py-2 text-sm bg-white" data-testid="patient-sex">
                <option value="">Selecionar</option><option value="F">Feminino</option><option value="M">Masculino</option><option value="X">Prefiro não dizer</option>
              </select>
            ) : (
              <p className="text-sm text-brand-text" data-testid="patient-sex-value">{({ F: 'Feminino', M: 'Masculino', X: 'Prefiro não dizer' })[patient.sex] || '-'}</p>
            )}
          </div>
          {isBabyPatient(patient) && (
            <div className="space-y-2">
              <div><Label className="text-xs text-brand-muted mb-1 block">Sexo do bebé</Label>
                <select value={patient.baby_sex || ''} onChange={async e => { await axios.put(`${API}/api/patients/${patient.user_id}`, { baby_sex: e.target.value }); onRefresh(); }} className="w-full border border-brand-border rounded-xl px-3 py-2 text-sm bg-white" data-testid="baby-sex">
                  <option value="">Selecionar</option><option value="F">Feminino</option><option value="M">Masculino</option>
                </select>
              </div>
              {renderField({ label: "Data de nascimento do bebé", field: "baby_birth_date", type: "date" })}
            </div>
          )}
        </div>

        {/* Identificação */}
        <div className="mt-4 pt-3 border-t border-brand-border/50">
          <p className="text-[10px] text-brand-muted uppercase tracking-wider mb-2">Identificação</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {renderField({ label: "N.o Cidadão / BI", field: "citizen_id" })}
            {renderField({ label: "NIF", field: "nif" })}
            {renderField({ label: "N.o Utente (SNS)", field: "sns_number" })}
            {renderField({ label: "Ocupação", field: "occupation" })}
            {renderField({ label: "Morada (rua)", field: "street" })}
            {renderField({ label: "Código postal", field: "postal_code" })}
            {renderField({ label: "Localidade", field: "city" })}
            {renderField({ label: "Nome do encarregado de educação", field: "guardian_name" })}
            {renderField({ label: "Emails do responsável / família (separados por vírgula)", field: "guardian_emails" })}
          </div>
        </div>

        {/* Administrativo */}
        <div className="mt-4 pt-3 border-t border-brand-border/50">
          <p className="text-[10px] text-brand-muted uppercase tracking-wider mb-2">Administrativo</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {renderField({ label: "N.o Processo interno", field: "process_number" })}
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Local de consulta</Label>
              {editing ? (
                <select value={form.consultation_location || ''} onChange={e => setForm({...form, consultation_location: e.target.value})} className="w-full border border-brand-border rounded-xl px-3 py-2 text-sm bg-white" data-testid="consultation-location">
                  <option value="">Selecionar...</option>
                  {locations.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              ) : (
                <p className="text-sm text-brand-text">{patient.consultation_location || '-'}</p>
              )}
            </div>
            {renderField({ label: "Seguradora", field: "insurer_name" })}
            {renderField({ label: "N.o Apólice", field: "insurer_policy" })}
          </div>
        </div>

        {/* Finalidade Terapêutica — IVA art. 9.º */}
        <div className="mt-4 pt-3 border-t border-brand-border/50" data-testid="therapeutic-purpose-section">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] text-brand-muted uppercase tracking-wider">
              Finalidade Terapêutica <span className="normal-case text-[9px] text-brand-placeholder">(isenção IVA art. 9.º)</span>
            </p>
            <div className="flex items-center gap-2">
              {(patient.therapeutic_purpose_history || []).length > 0 && (
                <button onClick={() => setShowTpHistory(true)} className="text-[10px] text-brand-green hover:underline" data-testid="tp-history-btn">Ver alterações</button>
              )}
              {!tpEditing ? (
                <button onClick={() => { setTpDraft(patient.therapeutic_purpose || ''); setTpEditing(true); }} className="text-xs text-brand-green flex items-center gap-1" data-testid="edit-tp-btn"><PencilSimple size={12} /> Editar</button>
              ) : (
                <div className="flex gap-2">
                  <button onClick={() => setTpEditing(false)} className="text-xs text-brand-muted">Cancelar</button>
                  <button
                    onClick={async () => {
                      setTpSaving(true);
                      try {
                        await axios.put(`${API}/api/patients/${pid}/therapeutic-purpose`, { therapeutic_purpose: tpDraft });
                        toast.success('Finalidade guardada');
                        onRefresh();
                        setTpEditing(false);
                      } catch { toast.error('Erro ao guardar'); }
                      finally { setTpSaving(false); }
                    }}
                    disabled={tpSaving}
                    className="text-xs text-brand-green font-medium disabled:opacity-50"
                    data-testid="save-tp-btn"
                  >{tpSaving ? 'A guardar...' : 'Guardar'}</button>
                </div>
              )}
            </div>
          </div>
          {tpEditing ? (
            <div>
              <Textarea
                value={tpDraft}
                onChange={e => setTpDraft(e.target.value.slice(0, 500))}
                rows={3}
                maxLength={500}
                placeholder="Ex: Acompanhamento nutricional clínico para tratamento e/ou prevenção de patologia..."
                className="rounded-xl border-brand-border text-sm"
                data-testid="tp-textarea"
              />
              <p className="text-[10px] text-brand-placeholder mt-1 text-right">{tpDraft.length}/500</p>
            </div>
          ) : !patient.therapeutic_purpose ? (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-2" data-testid="tp-missing-warning">
              <Warning size={14} className="text-amber-600 flex-shrink-0" />
              <p className="text-xs text-amber-700">Falta registar a finalidade terapêutica</p>
            </div>
          ) : (
            <div data-testid="tp-value-display">
              <p className="text-sm text-brand-text whitespace-pre-line">{patient.therapeutic_purpose}</p>
              {patient.therapeutic_purpose_filled_at && (
                <p className="text-[10px] text-brand-placeholder mt-1">
                  Preenchido por {patient.therapeutic_purpose_filled_by_name || '?'} em {fmtDatePt(patient.therapeutic_purpose_filled_at.slice(0, 10))}
                </p>
              )}
            </div>
          )}
          <FieldVersionHistoryModal
            open={showTpHistory}
            onOpenChange={setShowTpHistory}
            fieldLabel="Finalidade Terapêutica"
            history={patient.therapeutic_purpose_history || []}
            onRestore={(text) => { setTpDraft(text); setTpEditing(true); setShowTpHistory(false); }}
          />
        </div>

        {/* WhatsApp opt-in toggle */}
        <div className="mt-3 bg-white border border-brand-border rounded-xl p-3 flex items-center justify-between" data-testid="whatsapp-opt-in-row">
          <div>
            <p className="text-xs font-medium text-brand-text">Mensagens automáticas WhatsApp</p>
            <p className="text-[10px] text-brand-muted">Receber lembretes de consulta e pedidos de feedback no WhatsApp.</p>
          </div>
          <button
            onClick={async () => {
              const next = patient.whatsapp_opt_in === false ? true : false;
              try {
                await axios.put(`${API}/api/patients/${patient.user_id}`, { whatsapp_opt_in: next });
                toast.success(next ? 'WhatsApp ativado' : 'WhatsApp desativado');
                onRefresh();
              } catch { toast.error('Erro'); }
            }}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${patient.whatsapp_opt_in === false ? 'bg-slate-300' : 'bg-emerald-500'}`}
            data-testid="whatsapp-opt-in-toggle"
          >
            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${patient.whatsapp_opt_in === false ? 'translate-x-1' : 'translate-x-5'}`} />
          </button>
        </div>
        {/* Em acompanhamento activo toggle (Métricas: base de retenção/churn) */}
        <div className="mt-3 bg-white border border-brand-border rounded-xl p-3 flex items-center justify-between" data-testid="active-followup-row">
          <div>
            <p className="text-xs font-medium text-brand-text">Em acompanhamento activo</p>
            <p className="text-[10px] text-brand-muted">Conta para as métricas de retenção e churn. Desativa para clientes antigas/sem acompanhamento real.</p>
          </div>
          <button
            onClick={async () => {
              const next = patient.active_followup === false ? true : false;
              try {
                await axios.put(`${API}/api/patients/${patient.user_id}`, { active_followup: next });
                toast.success(next ? 'Marcada em acompanhamento activo' : 'Removida do acompanhamento activo');
                onRefresh();
              } catch { toast.error('Erro'); }
            }}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${patient.active_followup === false ? 'bg-slate-300' : 'bg-emerald-500'}`}
            data-testid="active-followup-toggle"
          >
            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${patient.active_followup === false ? 'translate-x-1' : 'translate-x-5'}`} />
          </button>
        </div>
        {/* Custom Fields */}
        <CustomFieldsSection patient={patient} onRefresh={onRefresh} />
        {/* Content case toggle */}
        <div className="mt-4 pt-3 border-t border-brand-border/50" data-testid="content-case-section">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] text-brand-muted uppercase tracking-wider">Caso para conteúdo</p>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={!!patient.content_case} onChange={async e => { await axios.put(`${API}/api/patients/${patient.user_id}`, { content_case: e.target.checked }); onRefresh(); }} className="rounded border-brand-border w-4 h-4" data-testid="content-case-toggle" />
              <span className="text-[10px] text-brand-muted">Caso interessante</span>
            </label>
          </div>
          {patient.content_case && (
            <Textarea value={patient.content_case_notes || ''} onBlur={async e => { await axios.put(`${API}/api/patients/${patient.user_id}`, { content_case_notes: e.target.value }); }} placeholder="Notas sobre este caso..." className="rounded-xl border-brand-border text-xs" rows={2} data-testid="content-case-notes" />
          )}
        </div>
      </div>
      </>)}

      {section === 'consultas' && (<>
      {/* Appointments */}
      <div className="bg-white border border-brand-border rounded-2xl p-4">
        <div className="flex justify-between mb-3">
          <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider">Consultas agendadas</h3>
          <button onClick={() => setShowAptForm(true)} className="text-xs text-brand-green flex items-center gap-1" data-testid="add-appointment-btn"><Plus size={12} /> Agendar</button>
        </div>
        {appointments.length === 0 ? (
          <p className="text-xs text-brand-placeholder">Sem consultas agendadas.</p>
        ) : (
          <div className="space-y-2">
            {appointments.map(a => {
              const calUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(`Consulta - ${patient.name} (${a.type})`)}&dates=${(a.date||'').replace(/-/g,'')}T${(a.time||'10:00').replace(/:/g,'')}00/${(a.date||'').replace(/-/g,'')}T${(a.time||'11:00').replace(/:/g,'')}00&details=${encodeURIComponent(`Paciente: ${patient.name}\nTipo: ${a.type}\n${a.location ? 'Local: '+a.location+'\n' : ''}${a.link ? 'Link: '+a.link+'\n' : ''}${a.notes ? 'Notas: '+a.notes : ''}`)}&location=${encodeURIComponent(a.location || '')}`;
              return (
                <div key={a.id} className="p-3 bg-brand-bg/50 rounded-xl border border-brand-border/40">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-brand-text">{fmtDateTime(a.date, a.time)}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] bg-brand-bg text-brand-muted px-2 py-0.5 rounded-full">{a.type}</span>
                        {a.location && <span className="text-[10px] text-brand-muted">{a.location}</span>}
                      </div>
                      {a.link && <a href={a.link} target="_blank" rel="noreferrer" className="text-[10px] text-brand-green hover:underline mt-0.5 block">Link da videochamada</a>}
                      {a.notes && <Md as="p" className="text-[10px] text-brand-muted mt-1">{a.notes}</Md>}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <a href={calUrl} target="_blank" rel="noreferrer" className="p-1.5 text-brand-green hover:bg-brand-green/10 rounded-lg transition-colors" title="Adicionar ao Google Calendar" data-testid={`apt-calendar-${a.id}`}><CalendarCheck size={14} /></a>
                      <a href={`${API}/api/appointments/${a.id}/ics`} download className="p-1.5 text-brand-green hover:bg-brand-green/10 rounded-lg transition-colors" title="Descarregar .ics" data-testid={`apt-ics-${a.id}`}><FilePdf size={14} /></a>
                      <button onClick={() => deleteApt(a.id)} className="p-1.5 text-brand-placeholder hover:text-red-500"><Trash size={13} /></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Histórico cancelamentos/remarcações — recolhido por defeito */}
      {appointmentHistory.length > 0 && (
        <div className="bg-white border border-brand-border rounded-2xl p-4" data-testid="apt-history-card">
          <button onClick={() => setShowApptHistory(v => !v)} className="w-full flex justify-between items-center mb-0" data-testid="apt-history-toggle">
            <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider flex items-center gap-1.5"><CaretDown size={12} className={`transition-transform ${showApptHistory ? 'rotate-180' : ''}`} /> Histórico de cancelamentos e remarcações</h3>
            <span className="text-[10px] text-brand-muted">{appointmentHistory.length} registos</span>
          </button>
          {showApptHistory && (
          <div className="space-y-2 mt-3">
            {appointmentHistory.map(a => {
              const isCanc = a.status === 'cancelled';
              return (
                <div key={a.id} className="p-3 rounded-xl border" style={{ backgroundColor: isCanc ? '#F3F4F6' : '#FEF3E2', borderColor: isCanc ? '#E5E7EB' : '#F59E0B33' }} data-testid={`apt-history-${a.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-brand-text">{fmtDateTime(a.date, a.time)}</p>
                      <p className="text-[10px] font-semibold mt-0.5" style={{ color: isCanc ? '#6B7280' : '#D97706' }}>{isCanc ? 'Cancelada' : 'Remarcada'}</p>
                      {(a.cancel_internal_reason || a.cancelled_reason) && <p className="text-[11px] text-brand-muted mt-1" data-testid="history-internal-reason"><span className="font-medium text-brand-text">Motivo interno:</span> {a.cancel_internal_reason || a.cancelled_reason}</p>}
                      {a.rescheduled_reason && <p className="text-[11px] text-brand-muted mt-1"><span className="font-medium text-brand-text">Motivo:</span> {a.rescheduled_reason}</p>}
                      {a.cancelled_at && <p className="text-[9px] text-brand-placeholder mt-1">Registado em {fmtDay(a.cancelled_at)}</p>}
                      {a.rescheduled_at && <p className="text-[9px] text-brand-placeholder mt-1">Registado em {fmtDay(a.rescheduled_at)}</p>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          )}
        </div>
      )}
      </>)}

      {section === 'clinico' && (<>
      <div className="bg-white border border-brand-border rounded-2xl p-4">
        <div className="flex justify-between mb-3">
          <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider">Suplementação</h3>
          <div className="flex gap-2 items-center">
            {supplements.length > 0 && (
              <button onClick={() => window.open(`${API}/api/patients/${pid}/supplementation/pdf`, '_blank')} className="text-xs text-brand-green flex items-center gap-1" data-testid="supplements-pdf-btn"><FilePdf size={12} /> PDF</button>
            )}
            <button onClick={openSuppHistory} className="text-xs text-brand-muted flex items-center gap-1" data-testid="supplements-history-btn"><ClockCounterClockwise size={12} /> Histórico</button>
            <button onClick={() => { setSuppItems(supplements.length > 0 ? [...supplements] : [{ name: '', dose: '', frequency: '', time: '', notes: '' }]); setShowSuppForm(true); }} className="text-xs text-brand-green flex items-center gap-1" data-testid="edit-supplements-btn"><PencilSimple size={12} /> Editar</button>
          </div>
        </div>
        {supplements.length === 0 ? (
          <p className="text-xs text-brand-placeholder">Sem suplementação definida.</p>
        ) : (
          <div className="space-y-1.5">
            {supplements.map((s, i) => (
              <div key={i} className="flex items-center justify-between p-2 bg-brand-bg/50 rounded-xl">
                <div className="flex items-center gap-2">
                  {s.image_path && <img src={`${API}/api/files/${s.image_path}`} alt="" className="w-8 h-8 object-cover rounded-lg" />}
                  <div><p className="text-sm text-brand-text">{s.name}</p><p className="text-[10px] text-brand-muted">{s.dose}{s.frequency ? ` · ${s.frequency}` : ''}{s.notes ? ` · ${s.notes}` : ''}</p></div>
                </div>
                {s.time && <span className="text-xs text-brand-muted">{s.time}</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pedido de Exames Laboratoriais */}
      <div className="bg-white border border-brand-border rounded-2xl p-4">
        <div className="flex justify-between items-center">
          <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider">Pedido de Exames</h3>
          <div className="flex items-center gap-3">
            <button onClick={openLabHistory} className="text-xs text-brand-muted flex items-center gap-1" data-testid="open-lab-history-btn"><ClockCounterClockwise size={12} /> Histórico</button>
            <button onClick={() => setShowLabForm(true)} className="text-xs text-brand-green flex items-center gap-1" data-testid="open-lab-request-btn"><TestTube size={12} /> Pedido de Exames</button>
          </div>
        </div>
        <p className="text-[11px] text-brand-placeholder mt-1">Gera um PDF de requisição de análises laboratoriais para a paciente levar ao médico.</p>
      </div>
      </>)}

      {section === 'documentos' && (<>
      {/* Declarações */}
      <div className="bg-white border border-brand-border rounded-2xl p-4">
        <div className="flex justify-between items-center">
          <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider">Declarações</h3>
          <div className="flex items-center gap-3">
            <button onClick={openDeclHistory} className="text-xs text-brand-muted flex items-center gap-1" data-testid="open-declaration-history-btn"><ClockCounterClockwise size={12} /> Histórico</button>
            <button onClick={openDeclForm} className="text-xs text-brand-green flex items-center gap-1" data-testid="open-declaration-btn"><FileText size={12} /> Declaração</button>
          </div>
        </div>
        <p className="text-[11px] text-brand-placeholder mt-1">Gera declarações (escola/creche, presença em consulta, ou texto livre) em PT ou EN.</p>
      </div>
      </>)}

      {section === 'dados' && (<>
      {/* Check-in day */}
      <div className="bg-white border border-brand-border rounded-2xl p-4">
        <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-3">Dia de check-in</h3>
        <div className="flex gap-1.5">
          {dayNames.map((d, i) => (
            <button key={i} onClick={() => saveCheckinDay(i)} className={`flex-1 py-2 rounded-xl text-xs font-medium transition-all ${checkinDay === i ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`} data-testid={`checkin-day-${i}`}>{d}</button>
          ))}
        </div>
      </div>

      {/* Reset password */}
      <div className="bg-white border border-brand-border rounded-2xl p-4">
        <div className="flex justify-between items-center gap-2 flex-wrap">
          <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider">Acesso</h3>
          <div className="flex items-center gap-4">
            <button onClick={async () => { setResendingAccess(true); try { const { data } = await axios.post(`${API}/api/patients/${pid}/resend-access`, {}); toast.success(data.message || 'Email de acesso enviado'); } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao enviar acesso'); } finally { setResendingAccess(false); } }} disabled={resendingAccess} className="text-xs text-brand-green flex items-center gap-1 disabled:opacity-50" data-testid="resend-access-btn"><PaperPlaneTilt size={12} /> {resendingAccess ? 'A enviar...' : 'Reenviar acesso'}</button>
            <button onClick={() => { setNewPassword(''); setShowResetPw(true); }} className="text-xs text-brand-green flex items-center gap-1" data-testid="reset-password-btn"><PencilSimple size={12} /> Redefinir palavra-passe</button>
          </div>
        </div>
        <p className="text-[11px] text-brand-muted mt-2">"Reenviar acesso" envia à paciente um email com link seguro (válido 7 dias) para definir a palavra-passe.</p>
      </div>
      </>)}

      {/* ═══ DIALOGS ═══ */}
      {/* Reset password */}
      <Dialog open={showResetPw} onOpenChange={setShowResetPw}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Redefinir palavra-passe</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-brand-muted">Define uma nova palavra-passe para <b>{patient.name}</b>. Comunica a nova palavra-passe à paciente.</p>
            <div><Label className="text-xs text-brand-muted mb-1 block">Nova palavra-passe</Label><Input type="text" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Mínimo 6 caracteres" className="rounded-xl border-brand-border" data-testid="new-password-input" /></div>
            <button onClick={async () => { try { await axios.put(`${API}/api/patients/${pid}/reset-password`, { password: newPassword }); toast.success('Palavra-passe redefinida'); setShowResetPw(false); } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); } }} disabled={newPassword.length < 6} className="w-full bg-brand-green text-white rounded-full py-3 font-medium disabled:opacity-50" data-testid="confirm-reset-pw">Redefinir</button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Appointment form */}
      <Dialog open={showAptForm} onOpenChange={setShowAptForm}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Agendar consulta</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-brand-muted mb-1 block">Data</Label><Input type="date" value={aptForm.date} onChange={e => setAptForm({ ...aptForm, date: e.target.value })} className="rounded-xl border-brand-border" required data-testid="apt-date" /></div>
              <div><Label className="text-xs text-brand-muted mb-1 block">Hora</Label><Input type="time" value={aptForm.time} onChange={e => setAptForm({ ...aptForm, time: e.target.value })} className="rounded-xl border-brand-border" data-testid="apt-time" /></div>
            </div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Tipo</Label>
              <div className="flex gap-2">
                {['presencial', 'online'].map(t => <button key={t} type="button" onClick={() => setAptForm({ ...aptForm, type: t })} className={`flex-1 px-3 py-2 rounded-xl text-xs font-medium ${aptForm.type === t ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>{t}</button>)}
              </div>
            </div>
            {aptForm.type === 'presencial' && (
              <div><Label className="text-xs text-brand-muted mb-1 block">Localização</Label><Input value={aptForm.location} onChange={e => setAptForm({ ...aptForm, location: e.target.value })} placeholder="Ex: Clínica nutrianapereira, Lisboa" className="rounded-xl border-brand-border" data-testid="apt-location" /></div>
            )}
            {aptForm.type === 'online' && (
              <div><Label className="text-xs text-brand-muted mb-1 block">Link da videochamada</Label><Input value={aptForm.link} onChange={e => setAptForm({ ...aptForm, link: e.target.value })} placeholder="https://meet.google.com/..." className="rounded-xl border-brand-border" data-testid="apt-link" /></div>
            )}
            <div><Label className="text-xs text-brand-muted mb-1 block">Notas (opcional)</Label><Input value={aptForm.notes} onChange={e => setAptForm({ ...aptForm, notes: e.target.value })} placeholder="Ex: Trazer análises" className="rounded-xl border-brand-border" /></div>
            <button onClick={saveApt} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="save-apt-btn">Agendar</button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Supplements editor */}
      <Dialog open={showSuppForm} onOpenChange={setShowSuppForm}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Suplementação</DialogTitle></DialogHeader>
          <div className="space-y-2 mb-3">
            {suppItems.map((s, i) => (
              <div key={i} className="flex flex-col gap-1.5 p-2 bg-brand-bg/40 rounded-xl">
                <div className="flex gap-2 items-center">
                  {s.image_path && <img src={`${API}/api/files/${s.image_path}`} alt="" className="w-8 h-8 object-cover rounded-lg flex-shrink-0" />}
                  <Input value={s.name} onChange={e => { const u = [...suppItems]; u[i] = { ...u[i], name: e.target.value }; setSuppItems(u); }} placeholder="Nome" className="flex-1 rounded-xl border-brand-border text-xs" data-testid={`supp-name-${i}`} />
                  <Input value={s.dose} onChange={e => { const u = [...suppItems]; u[i] = { ...u[i], dose: e.target.value }; setSuppItems(u); }} placeholder="Dose" className="w-24 rounded-xl border-brand-border text-xs" data-testid={`supp-dose-${i}`} />
                  <button onClick={() => setSuppItems(suppItems.filter((_, j) => j !== i))} className="text-red-400" data-testid={`supp-remove-${i}`}><Trash size={12} /></button>
                </div>
                <div className="flex gap-2">
                  <Input value={s.frequency || ''} onChange={e => { const u = [...suppItems]; u[i] = { ...u[i], frequency: e.target.value }; setSuppItems(u); }} placeholder="Frequência (ex: Diária)" className="w-1/2 rounded-xl border-brand-border text-xs" data-testid={`supp-frequency-${i}`} />
                  <Input value={s.time} onChange={e => { const u = [...suppItems]; u[i] = { ...u[i], time: e.target.value }; setSuppItems(u); }} placeholder="Horário" className="w-1/2 rounded-xl border-brand-border text-xs" data-testid={`supp-time-${i}`} />
                </div>
                <Input value={s.notes || ''} onChange={e => { const u = [...suppItems]; u[i] = { ...u[i], notes: e.target.value }; setSuppItems(u); }} placeholder="Observação (opcional, específica desta paciente)" className="rounded-xl border-brand-border text-xs" data-testid={`supp-notes-${i}`} />
              </div>
            ))}
            <div className="flex gap-2 items-center pt-1">
              <button onClick={() => setSuppItems([...suppItems, { name: '', dose: '', frequency: '', time: '', notes: '' }])} className="text-xs text-brand-green flex items-center gap-1" data-testid="supp-add-avulso-btn"><Plus size={12} /> Avulso</button>
              {suppLibrary.length > 0 && (
                <select onChange={e => {
                  const lib = suppLibrary.find(l => l.id === e.target.value);
                  if (lib) setSuppItems([...suppItems, { name: lib.name, dose: lib.default_dose || '', frequency: '', time: '', notes: '', indication: lib.default_indication || '', image_path: lib.image_path || '', library_id: lib.id }]);
                  e.target.value = '';
                }} defaultValue="" className="text-xs border border-brand-border rounded-xl px-2 py-1.5 bg-white flex-1" data-testid="supp-add-from-library">
                  <option value="">+ Da biblioteca...</option>
                  {suppLibrary.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              )}
            </div>
          </div>
          <button onClick={saveSupps} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="save-supplements-btn">Guardar</button>
        </DialogContent>
      </Dialog>
      {/* Supplements history */}
      <Dialog open={showSuppHistory} onOpenChange={setShowSuppHistory}>
        <DialogContent className="rounded-3xl border-brand-border max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Histórico de suplementação</DialogTitle></DialogHeader>
          {suppHistory.length === 0 ? (
            <p className="text-xs text-brand-placeholder" data-testid="supp-history-empty">Sem prescrições anteriores registadas.</p>
          ) : (
            <div className="space-y-3" data-testid="supp-history-list">
              {suppHistory.map((h, hi) => (
                <div key={h.id || hi} className="border border-brand-border rounded-xl p-3">
                  <p className="text-[10px] text-brand-muted mb-2">{h.start_date?.slice(0, 10)} a {h.end_date?.slice(0, 10)}</p>
                  <div className="space-y-1">
                    {(h.items || []).map((it, ii) => (
                      <p key={ii} className="text-xs text-brand-text">{it.name}, {it.dose}{it.time ? ` · ${it.time}` : ''}</p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Pedido de Exames Laboratoriais */}
      <Dialog open={showLabForm} onOpenChange={setShowLabForm}>
        <DialogContent className="rounded-3xl border-brand-border max-h-[85vh] overflow-y-auto max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Pedido de Exames Laboratoriais</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-brand-muted mb-1 block">Nome do médico (opcional)</Label><Input value={labDoctor} onChange={e => setLabDoctor(e.target.value)} placeholder="Ex: Dr. João Silva" className="rounded-xl border-brand-border text-sm" data-testid="lab-doctor-name" /></div>
            <div className="grid grid-cols-2 gap-4 max-h-[45vh] overflow-y-auto pr-1">
              <div className="space-y-1.5">
                {LAB_EXAMS_COL1.map(name => (
                  <label key={name} className="flex items-center gap-1.5 text-xs text-brand-text cursor-pointer">
                    <input type="checkbox" checked={!!labExams[name]} onChange={() => toggleLabExam(name)} className="rounded border-brand-border" data-testid={`lab-exam-${labSlug(name)}`} />
                    {name}
                  </label>
                ))}
                <label className="flex items-center gap-1.5 text-xs text-brand-text cursor-pointer">
                  <input type="checkbox" checked={labOutrosChecked} onChange={() => setLabOutrosChecked(v => !v)} className="rounded border-brand-border flex-shrink-0" data-testid="lab-exam-outros-checkbox" />
                  <span className="flex-shrink-0">Outros:</span>
                  <Input value={labOutrosText} onChange={e => setLabOutrosText(e.target.value)} placeholder="especificar" className="h-6 text-xs rounded-lg border-brand-border flex-1 px-2" data-testid="lab-exam-outros-text" />
                </label>
              </div>
              <div className="space-y-1.5">
                {LAB_EXAMS_COL2.map(name => (
                  <label key={name} className="flex items-center gap-1.5 text-xs text-brand-text cursor-pointer">
                    <input type="checkbox" checked={!!labExams[name]} onChange={() => toggleLabExam(name)} className="rounded border-brand-border" data-testid={`lab-exam-${labSlug(name)}`} />
                    {name}
                  </label>
                ))}
              </div>
            </div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Nota para o médico (vai no PDF, a paciente também vê)</Label><Textarea value={labNotes} onChange={e => setLabNotes(e.target.value)} className="rounded-xl border-brand-border text-sm" rows={2} data-testid="lab-notes" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Mensagem para a paciente (só na app)</Label><Textarea value={labNotesPaciente} onChange={e => setLabNotesPaciente(e.target.value)} className="rounded-xl border-brand-border text-sm" rows={2} placeholder="Ex: Faz este exame em jejum e avisa-me quando tiveres os resultados." data-testid="lab-notes-paciente" /></div>
            <button onClick={generateLabPdf} disabled={generatingLabPdf} className="w-full bg-brand-green text-white rounded-full py-3 font-medium disabled:opacity-50" data-testid="generate-lab-pdf-btn">{generatingLabPdf ? 'A gerar...' : 'Gerar PDF'}</button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Histórico de Pedidos de Exames */}
      <Dialog open={showLabHistory} onOpenChange={setShowLabHistory}>
        <DialogContent className="rounded-3xl border-brand-border max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Histórico de Pedidos de Exames</DialogTitle></DialogHeader>
          {labHistory.length === 0 ? (
            <p className="text-xs text-brand-placeholder" data-testid="lab-history-empty">Sem pedidos de exames anteriores.</p>
          ) : (
            <div className="space-y-2" data-testid="lab-history-list">
              {labHistory.map(h => (
                <div key={h.id} className="border border-brand-border rounded-xl p-3 flex items-center justify-between gap-2" data-testid={`lab-history-item-${h.id}`}>
                  <div>
                    <p className="text-xs font-medium text-brand-text">{h.created_at.slice(0, 10)} {h.doctor_name ? `· ${/^dr[a.]*\s/i.test(h.doctor_name) ? h.doctor_name : `Dr(a). ${h.doctor_name}`}` : ''}</p>
                    <p className="text-[10px] text-brand-muted">{(h.selected_exams || []).length} exame(s) selecionado(s){h.notes ? ' · com notas' : ''}</p>
                  </div>
                  <button onClick={() => redownloadLabRequest(h)} className="text-xs text-brand-green flex items-center gap-1 flex-shrink-0" data-testid={`lab-history-download-${h.id}`}><FilePdf size={14} /> Descarregar</button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Gerar Declaração */}
      <Dialog open={showDeclForm} onOpenChange={setShowDeclForm}>
        <DialogContent className="rounded-3xl border-brand-border max-h-[85vh] overflow-y-auto max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Gerar Declaração</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {cedulaMissing && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2" data-testid="declaration-no-cedula-warning">
                <Warning size={16} className="text-red-500 flex-shrink-0" />
                <p className="text-xs text-red-600">Não tens cédula profissional definida no teu perfil. Pede a um admin para a definir em Equipa antes de gerar declarações.</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-brand-muted mb-1 block">Modelo</Label>
                <select value={declForm.template_id} onChange={e => { setDeclForm({ ...declForm, template_id: e.target.value, final_text: '' }); setDeclLocationWarning(false); }} className="w-full border border-brand-border rounded-xl px-3 py-2 text-sm bg-white" data-testid="declaration-template-select">
                  <option value="">Selecionar...</option>
                  {declTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs text-brand-muted mb-1 block">Língua</Label>
                <div className="flex gap-2">
                  {[['pt', 'PT'], ['en', 'EN']].map(([v, l]) => (
                    <button key={v} type="button" onClick={() => { setDeclForm({ ...declForm, language: v, final_text: '' }); setDeclLocationWarning(false); }} className={`flex-1 py-2 rounded-xl text-xs font-medium ${declForm.language === v ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`} data-testid={`declaration-lang-${v}`}>{l}</button>
                  ))}
                </div>
              </div>
            </div>
            {declNeedsConsult && (
              <div>
                <Label className="text-xs text-brand-muted mb-1 block">Consulta</Label>
                <select value={declForm.consultation_id} onChange={e => onPickDeclConsultation(e.target.value)} className="w-full border border-brand-border rounded-xl px-3 py-2 text-sm bg-white" data-testid="declaration-consultation-select">
                  <option value="">Selecionar...</option>
                  {declConsultations.map(c => <option key={c.id} value={c.id}>{fmtDatePt(c.date)}{c.modality ? ` · ${c.modality}` : ''}</option>)}
                </select>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div><Label className="text-xs text-brand-muted mb-1 block">Hora início</Label><Input type="time" value={declForm.hora_inicio} onChange={e => setDeclForm({ ...declForm, hora_inicio: e.target.value })} className="rounded-xl border-brand-border" data-testid="declaration-hora-inicio" /></div>
                  <div><Label className="text-xs text-brand-muted mb-1 block">Hora fim</Label><Input type="time" value={declForm.hora_fim} onChange={e => setDeclForm({ ...declForm, hora_fim: e.target.value })} className="rounded-xl border-brand-border" data-testid="declaration-hora-fim" /></div>
                </div>
              </div>
            )}
            {(declTags.has('nome_acompanhante') || declTags.has('nome_encarregado')) && (
              <div><Label className="text-xs text-brand-muted mb-1 block">Nome do acompanhante</Label><Input value={declForm.nome_acompanhante} onChange={e => setDeclForm({ ...declForm, nome_acompanhante: e.target.value })} className="rounded-xl border-brand-border text-sm" data-testid="declaration-acompanhante" /></div>
            )}
            {declTags.has('escola') && (
              <div><Label className="text-xs text-brand-muted mb-1 block">Escola / creche</Label><Input value={declForm.escola} onChange={e => setDeclForm({ ...declForm, escola: e.target.value })} className="rounded-xl border-brand-border text-sm" data-testid="declaration-escola" /></div>
            )}
            {declTags.has('conteudo') && (
              <div><Label className="text-xs text-brand-muted mb-1 block">Conteúdo</Label><Textarea value={declForm.conteudo} onChange={e => setDeclForm({ ...declForm, conteudo: e.target.value })} className="rounded-xl border-brand-border text-sm" rows={3} data-testid="declaration-conteudo" /></div>
            )}
            <button onClick={previewDecl} disabled={previewingDecl || !declForm.template_id || cedulaMissing} className="text-xs text-brand-green font-medium disabled:opacity-50" data-testid="declaration-preview-btn">{previewingDecl ? 'A pré-visualizar...' : 'Pré-visualizar texto'}</button>
            {declLocationWarning && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-2" data-testid="declaration-location-warning">
                <Warning size={16} className="text-amber-500 flex-shrink-0" />
                <p className="text-xs text-amber-700">Esta paciente não tem um local de consulta registado, foi usada a morada do consultório por defeito. Podes editar o texto final se não for o pretendido.</p>
              </div>
            )}
            {declForm.final_text && (
              <div>
                <Label className="text-xs text-brand-muted mb-1 block">Texto final (editável, só altera esta declaração)</Label>
                <Textarea value={declForm.final_text} onChange={e => setDeclForm({ ...declForm, final_text: e.target.value })} className="rounded-xl border-brand-border text-sm" rows={8} data-testid="declaration-final-text" />
              </div>
            )}
            <label className="flex items-center gap-2 text-xs text-brand-text cursor-pointer">
              <input type="checkbox" checked={declForm.share_with_patient} onChange={e => setDeclForm({ ...declForm, share_with_patient: e.target.checked })} className="rounded border-brand-border" data-testid="declaration-share-checkbox" />
              Partilhar com a paciente (fica visível nos ficheiros dela)
            </label>
            <button onClick={generateDeclPdf} disabled={generatingDeclPdf || !declForm.final_text.trim() || cedulaMissing} className="w-full bg-brand-green text-white rounded-full py-3 font-medium disabled:opacity-50" data-testid="generate-declaration-pdf-btn">{generatingDeclPdf ? 'A gerar...' : 'Gerar PDF'}</button>
            {!cedulaMissing && declMissingFieldMsg() && (
              <p className="text-xs text-amber-600 text-center" data-testid="declaration-missing-field-hint">{declMissingFieldMsg()}</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {/* Histórico de Declarações */}
      <Dialog open={showDeclHistory} onOpenChange={setShowDeclHistory}>
        <DialogContent className="rounded-3xl border-brand-border max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Histórico de Declarações</DialogTitle></DialogHeader>
          {declHistory.length === 0 ? (
            <p className="text-xs text-brand-placeholder" data-testid="declaration-history-empty">Sem declarações anteriores.</p>
          ) : (
            <div className="space-y-2" data-testid="declaration-history-list">
              {declHistory.map(h => (
                <div key={h.id} className="border border-brand-border rounded-xl p-3 flex items-center justify-between gap-2" data-testid={`declaration-history-item-${h.id}`}>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-brand-text truncate">{h.created_at.slice(0, 10)} · {h.template_name} ({(h.language || 'pt').toUpperCase()})</p>
                    {h.shared_with_patient && <p className="text-[10px] text-brand-green">Partilhada com a paciente</p>}
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <button onClick={() => duplicateDecl(h)} className="text-xs text-brand-muted flex items-center gap-1" data-testid={`declaration-duplicate-${h.id}`}><Copy size={13} /> Duplicar</button>
                    <button onClick={() => redownloadDecl(h)} className="text-xs text-brand-green flex items-center gap-1" data-testid={`declaration-history-download-${h.id}`}><FilePdf size={14} /> Descarregar</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── ANAMNESE TAB (structured clinical blocks) ───




// ─── FOOD INTRODUCTIONS TAB (nutri view) ───
function FoodIntroFeedback({ patientId, intro, onSaved }) {
  const NUTRI_REACTIONS = [
    { value: 'boa_escolha', emoji: '👍', label: 'Boa escolha' },
    { value: 'vamos_ajustar', emoji: '⚠️', label: 'Vamos ajustar' },
    { value: 'adorei', emoji: '❤️', label: 'Adorei' },
  ];
  const [comment, setComment] = useState(intro.nutri_comment || '');
  const [reaction, setReaction] = useState(intro.nutri_reaction || '');
  const [saving, setSaving] = useState(false);
  const saveReaction = async (val) => {
    const next = reaction === val ? '' : val;
    setReaction(next);
    try { await axios.put(`${API}/api/patients/${patientId}/food-introductions/${intro.id}/feedback`, { nutri_reaction: next }); toast.success('Reação enviada à paciente'); onSaved && onSaved(); } catch { toast.error('Erro'); }
  };
  const saveComment = async () => {
    setSaving(true);
    try { await axios.put(`${API}/api/patients/${patientId}/food-introductions/${intro.id}/feedback`, { nutri_comment: comment }); toast.success('Comentário enviado à paciente'); onSaved && onSaved(); } catch { toast.error('Erro'); } finally { setSaving(false); }
  };
  return (
    <div className="mt-2 pt-2 border-t border-brand-border/40 space-y-2" data-testid={`intro-feedback-${intro.id}`}>
      {intro.photo_path && (
        <img src={`${API}/api/files/${intro.photo_path}`} alt="Foto da refeição" className="w-20 h-20 object-cover rounded-lg cursor-pointer border border-brand-border" data-testid={`intro-photo-${intro.id}`} onClick={() => window.open(`${API}/api/files/${intro.photo_path}`, '_blank')} />
      )}
      <div className="flex gap-1.5">
        {NUTRI_REACTIONS.map(r => (
          <button key={r.value} type="button" onClick={() => saveReaction(r.value)} data-testid={`nutri-reaction-${r.value}-${intro.id}`}
            className={`px-2 py-1 rounded-full text-[10px] font-medium border transition-all ${reaction === r.value ? 'bg-brand-green text-white border-brand-green' : 'bg-brand-bg text-brand-muted border-brand-border'}`}>
            {r.emoji} {r.label}
          </button>
        ))}
      </div>
      <div className="flex gap-1.5 items-end">
        <Textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="Comentário para a paciente..." rows={1} className="rounded-xl border-brand-border text-xs resize-none flex-1" data-testid={`nutri-comment-input-${intro.id}`} />
        <button onClick={saveComment} disabled={saving || comment === (intro.nutri_comment || '')} className="bg-brand-green text-white rounded-xl px-3 py-2 text-[10px] font-medium disabled:opacity-40 flex-shrink-0" data-testid={`nutri-comment-save-${intro.id}`}>Enviar</button>
      </div>
    </div>
  );
}

function FoodIntroductionsTab({ patientId }) {
  const [intros, setIntros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('timeline'); // timeline | list
  const fetch = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/patients/${patientId}/food-introductions`); setIntros(data); } catch {} finally { setLoading(false); }
  }, [patientId]);
  useEffect(() => { fetch(); }, [fetch]);
  const REACT_COLORS = { sem_reacao: 'bg-green-50 text-green-700', reacao_ligeira: 'bg-amber-50 text-amber-700', reacao_adversa: 'bg-red-50 text-red-700 font-semibold' };
  const REACT_LABELS = { sem_reacao: 'Sem reação', reacao_ligeira: 'Reação ligeira', reacao_adversa: 'Reação adversa' };
  const GROUP_LABELS = { energia: 'Energia', ferro_proteina: 'Ferro/Proteína', horticolas: 'Hortícolas', gordura: 'Gordura', micronutrientes: 'Micronutrientes' };
  const adverse = intros.filter(i => i.reaction === 'reacao_adversa');

  // Timeline: group by date
  const byDate = {};
  intros.forEach(i => { const d = i.introduced_date || '?'; if (!byDate[d]) byDate[d] = []; byDate[d].push(i); });
  const sortedDates = Object.keys(byDate).sort((a, b) => b.localeCompare(a));

  if (loading) return <div className="text-center py-8 text-brand-muted">A carregar...</div>;
  return (
    <div data-testid="tab-intro-alimentos">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-heading font-medium text-lg text-brand-text">Histórico de Introdução</h3>
          <p className="text-xs text-brand-muted">{intros.length} alimentos introduzidos</p>
        </div>
        <div className="flex gap-1">
          <button onClick={() => setViewMode('timeline')} className={`px-3 py-1.5 rounded-full text-xs font-medium ${viewMode === 'timeline' ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted'}`}>Timeline</button>
          <button onClick={() => setViewMode('list')} className={`px-3 py-1.5 rounded-full text-xs font-medium ${viewMode === 'list' ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted'}`}>Lista</button>
          <a href={`${API}/api/patients/${patientId}/food-introductions/pdf`} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-full text-xs font-medium bg-brand-bg text-brand-muted border border-brand-border hover:bg-brand-green/5">PDF</a>
        </div>
      </div>
      {adverse.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-3 mb-4">
          <p className="text-xs font-semibold text-red-700 mb-1">Reações adversas</p>
          {adverse.map(a => (
            <p key={a.id} className="text-xs text-red-600">· {a.food_name} ({a.introduced_date}){a.note ? `, ${a.note}` : ''}</p>
          ))}
        </div>
      )}
      {/* Timeline view */}
      {viewMode === 'timeline' && (
        <div className="space-y-3">
          {sortedDates.length === 0 ? <p className="text-center text-brand-muted py-8">Sem registos de introdução.</p> : sortedDates.map(date => (
            <div key={date} className="bg-white border border-brand-border rounded-2xl p-3">
              <p className="text-xs font-medium text-brand-green mb-2">{date}</p>
              <div className="space-y-2">
                {byDate[date].map(i => (
                  <div key={i.id} className="border-b border-brand-border/30 last:border-0 pb-2 last:pb-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-brand-text">{i.food_name}</span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${REACT_COLORS[i.reaction] || 'bg-brand-bg text-brand-muted'}`}>{REACT_LABELS[i.reaction] || i.reaction}</span>
                      {i.note && <span className="text-[9px] text-brand-placeholder">{i.note}</span>}
                      {i.photo_path && <span className="text-[9px] text-brand-green">📷</span>}
                    </div>
                    <FoodIntroFeedback patientId={patientId} intro={i} onSaved={fetch} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {/* List view */}
      {viewMode === 'list' && (
        <div className="space-y-1.5">
          {intros.map(i => (
            <div key={i.id} className={`bg-white border border-brand-border rounded-xl p-3 ${i.reaction === 'reacao_adversa' ? 'border-red-200' : ''}`} data-testid={`intro-${i.id}`}>
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-brand-text">{i.food_name}</span>
                    <span className="text-[9px] text-brand-placeholder">{GROUP_LABELS[i.group] || i.group}</span>
                    {i.photo_path && <span className="text-[9px] text-brand-green">📷</span>}
                  </div>
                  {i.note && <p className="text-[10px] text-brand-muted mt-0.5 truncate">{i.note}</p>}
                </div>
                <div className="text-right flex-shrink-0">
                  <span className={`text-[9px] px-2 py-0.5 rounded-full ${REACT_COLORS[i.reaction] || 'bg-brand-bg text-brand-muted'}`}>{REACT_LABELS[i.reaction] || i.reaction}</span>
                  <p className="text-[9px] text-brand-placeholder mt-0.5">{i.introduced_date}</p>
                </div>
              </div>
              <FoodIntroFeedback patientId={patientId} intro={i} onSaved={fetch} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── CHECKINS TAB (patient check-ins viewer + nutri registration) ───
const CHECKIN_DEFS = {
  gravida: [
    { key: 'semana_geral', label: 'Semana em geral', type: 'scale' },
    { key: 'peso', label: 'Peso (kg)', type: 'number' },
    { key: 'apetite', label: 'Apetite', type: 'scale' },
    { key: 'nauseas', label: 'Náuseas', type: 'choice', options: ['Sem náuseas', 'Melhorou', 'Igual', 'Piorou'] },
    { key: 'transito', label: 'Trânsito intestinal', type: 'choice', options: ['Normal', 'Obstipação', 'Diarreia'] },
    { key: 'sintomas', label: 'Sintomas', type: 'text' },
    { key: 'exercicio', label: 'Exercício', type: 'choice', options: ['Sim, regularmente', 'Sim, pontualmente', 'Não'] },
    { key: 'partilha', label: 'Observações', type: 'text' },
  ],
  pos_parto: [
    { key: 'semana_geral', label: 'Semana em geral', type: 'scale' },
    { key: 'peso', label: 'Peso (kg)', type: 'number' },
    { key: 'gestao_alimentar', label: 'Gestão alimentar', type: 'scale' },
    { key: 'energia', label: 'Energia', type: 'scale' },
    { key: 'transito', label: 'Trânsito intestinal', type: 'choice', options: ['Normal', 'Obstipação', 'Diarreia'] },
    { key: 'amamentacao', label: 'Amamentação', type: 'choice', options: ['Sim, corre bem', 'Sim, com dificuldades', 'Não amamenta'] },
    { key: 'sintomas', label: 'Sintomas', type: 'text' },
    { key: 'partilha', label: 'Observações', type: 'text' },
  ],
  emagrecimento: [
    { key: 'semana_geral', label: 'Semana em geral', type: 'scale' },
    { key: 'peso', label: 'Peso (kg)', type: 'number' },
    { key: 'cumprimento', label: 'Cumprimento do plano', type: 'scale' },
    { key: 'energia', label: 'Energia', type: 'scale' },
    { key: 'fome', label: 'Fome', type: 'choice', options: ['Muita fome', 'Fome normal', 'Pouco apetite', 'Irregular'] },
    { key: 'transito', label: 'Trânsito intestinal', type: 'choice', options: ['Normal', 'Obstipação', 'Diarreia'] },
    { key: 'exercicio', label: 'Exercício', type: 'choice', options: ['Sim, regularmente', 'Sim, pontualmente', 'Não'] },
    { key: 'partilha', label: 'Observações', type: 'text' },
  ],
  introducao_alimentar: [
    { key: 'semana_geral', label: 'Semana alimentar do bebé', type: 'scale' },
    { key: 'alimentos_novos', label: 'Alimentos novos', type: 'text' },
    { key: 'reacao', label: 'Reação', type: 'choice', options: ['Aceitou bem', 'Alguma resistência', 'Recusou'] },
    { key: 'transito', label: 'Trânsito intestinal', type: 'choice', options: ['Normal', 'Obstipação', 'Diarreia'] },
    { key: 'peso_bebe', label: 'Peso bebé (kg)', type: 'number' },
    { key: 'partilha', label: 'Observações', type: 'text' },
  ],
};
const CANAIS = ['Telefone', 'WhatsApp', 'Mensagem na app', 'Email', 'Consulta presencial', 'Outro'];
const RESULTADOS = ['Tudo bem, sem alterações', 'Reforço enviado', 'Vai preencher mais tarde', 'Reagendado', 'Outro'];

function CheckinsTab({ patientId, patient }) {
  const confirmDelete = useConfirmDelete();
  const [checkins, setCheckins] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [status, setStatus] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [showFullForm, setShowFullForm] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [formData, setFormData] = useState({});
  const [editingCheckinId, setEditingCheckinId] = useState(null);
  const [canal, setCanal] = useState('');
  const [nutriNotes, setNutriNotes] = useState('');
  const [contactForm, setContactForm] = useState({ date: new Date().toISOString().split('T')[0], time: new Date().toTimeString().slice(0,5), canal: '', resultado: '', notes: '' });

  const patientType = patient?.patient_type || 'emagrecimento';
  const fields = CHECKIN_DEFS[patientType] || CHECKIN_DEFS.emagrecimento;

  const fetchAll = useCallback(async () => {
    try {
      const [chk, st, an] = await Promise.all([
        axios.get(`${API}/api/patients/${patientId}/checkins`),
        axios.get(`${API}/api/patients/${patientId}/checkin-status`),
        axios.get(`${API}/api/patients/${patientId}/checkin-analytics`),
      ]);
      setCheckins(chk.data.checkins || []);
      setContacts(chk.data.contacts || []);
      setStatus(st.data);
      setAnalytics(an.data);
    } catch {}
  }, [patientId]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveFullCheckin = async () => {
    if (!canal) { toast.error('Selecciona o canal de recolha'); return; }
    try {
      const payload = {
        checkin_type: patientType,
        cumprimento: formData.cumprimento || formData.semana_geral || 3,
        energia: formData.energia || 3,
        fome: typeof formData.fome === 'number' ? formData.fome : 3,
        sintomas: formData.sintomas || '',
        extra_data: formData,
        canal, nutri_notes: nutriNotes,
      };
      if (editingCheckinId) {
        await axios.put(`${API}/api/patients/${patientId}/checkins/${editingCheckinId}`, payload);
        toast.success('Check-in atualizado');
      } else {
        await axios.post(`${API}/api/patients/${patientId}/checkins/nutri-register`, payload);
        toast.success('Check-in registado em nome da paciente');
      }
      setShowFullForm(false); setFormData({}); setCanal(''); setNutriNotes(''); setEditingCheckinId(null);
      fetchAll();
    } catch { toast.error('Erro ao guardar'); }
  };

  const saveContact = async () => {
    if (!contactForm.canal) { toast.error('Selecciona o canal'); return; }
    if (!contactForm.resultado) { toast.error('Selecciona o resultado'); return; }
    try {
      await axios.post(`${API}/api/patients/${patientId}/checkins/nutri-contact`, contactForm);
      toast.success('Contacto registado');
      setShowContactForm(false);
      setContactForm({ date: new Date().toISOString().split('T')[0], time: new Date().toTimeString().slice(0,5), canal: '', resultado: '', notes: '' });
      fetchAll();
    } catch { toast.error('Erro ao guardar'); }
  };

  // Merge checkins and contacts into timeline
  const timeline = [
    ...checkins.map(c => ({ ...c, _type: c.tipo_registo === 'pela_nutri' ? 'nutri' : 'paciente', _sort: c.date })),
    ...contacts.map(c => ({ ...c, _type: 'contacto', _sort: c.date })),
  ].sort((a, b) => b._sort.localeCompare(a._sort));

  const TYPE_STYLES = {
    paciente: { border: 'border-brand-green/30', bg: 'bg-brand-green/5', badge: 'bg-brand-green/10 text-brand-green', label: 'Pela paciente' },
    nutri: { border: 'border-amber-300/40', bg: 'bg-amber-50/50', badge: 'bg-amber-100 text-amber-700', label: 'Pela nutri' },
    contacto: { border: 'border-blue-300/40', bg: 'bg-blue-50/50', badge: 'bg-blue-100 text-blue-700', label: 'Contactada' },
  };

  return (
    <div data-testid="tab-checkins">
      {/* Status bar */}
      {status && (
        <div className={`rounded-2xl p-4 mb-4 border ${status.overdue ? 'bg-red-50/50 border-red-200' : status.has_checkin_this_week || status.has_contact_this_week ? 'bg-brand-green/5 border-brand-green/20' : 'bg-brand-bg border-brand-border'}`} data-testid="checkin-status">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-brand-text">
                {status.overdue ? 'Check-in em atraso' : status.has_checkin_this_week ? 'Check-in desta semana feito' : status.has_contact_this_week ? 'Contactada esta semana' : 'Check-in pendente'}
              </p>
              {status.designated_day_label && <p className="text-[10px] text-brand-muted mt-0.5">Dia designado: {status.designated_day_label}-feira</p>}
              {status.last_checkin_date && <p className="text-[10px] text-brand-muted">Último: {status.last_checkin_date}</p>}
            </div>
            {!(status.has_checkin_this_week) && (
              <div className="flex gap-1.5">
                <button onClick={() => { setFormData({}); setCanal(''); setNutriNotes(''); setShowFullForm(true); }} className="bg-brand-green text-white rounded-full px-3 py-2 text-[11px] font-medium" data-testid="register-checkin-btn">Registar check-in</button>
                <button onClick={() => setShowContactForm(true)} className="border border-brand-border text-brand-muted rounded-full px-3 py-2 text-[11px] font-medium hover:border-brand-green hover:text-brand-green" data-testid="register-contact-btn">Marcar contacto</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Analytics */}
      {analytics && analytics.total_checkins > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="bg-white border border-brand-border rounded-xl p-3 text-center">
            <p className="text-lg font-heading font-medium text-brand-green">{analytics.auto_fill_rate}%</p>
            <p className="text-[9px] text-brand-muted">Auto-preenchimento</p>
          </div>
          <div className="bg-white border border-brand-border rounded-xl p-3 text-center">
            <p className="text-lg font-heading font-medium text-amber-600">{analytics.intervention_rate}%</p>
            <p className="text-[9px] text-brand-muted">Intervenção nutri</p>
          </div>
          <div className="bg-white border border-brand-border rounded-xl p-3 text-center">
            <p className="text-lg font-heading font-medium text-brand-text">{analytics.total_checkins}</p>
            <p className="text-[9px] text-brand-muted">Total check-ins</p>
          </div>
        </div>
      )}

      {/* Timeline */}
      {timeline.length === 0 ? <p className="text-center text-brand-muted py-8">Sem check-ins registados.</p> : (
        <div className="space-y-3">
          {timeline.map(item => {
            const style = TYPE_STYLES[item._type] || TYPE_STYLES.paciente;
            return (
              <div key={item.id} className={`border rounded-2xl p-4 ${style.border} ${style.bg}`} data-testid={`checkin-item-${item.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-brand-text">{fmtDatePt(item.date)}</span>
                    <span className={`text-[9px] px-2 py-0.5 rounded-full font-medium ${style.badge}`}>{style.label}</span>
                    {item.checkin_type && item.checkin_type !== 'geral' && <span className="text-[9px] bg-brand-bg px-2 py-0.5 rounded-full text-brand-muted">{item.checkin_type}</span>}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {item.canal && <span className="text-[9px] text-brand-muted">{item.canal}</span>}
                    {/* Item 2 — Fase 5: edit + delete (nutri only) */}
                    {item._type !== 'contacto' && (
                      <button onClick={() => {
                        setFormData({ ...item, date: item.date, cumprimento: item.cumprimento, energia: item.energia, fome: item.fome, sintomas: item.sintomas, observacoes: item.observacoes });
                        setCanal(item.canal || ''); setNutriNotes(item.observacoes || ''); setEditingCheckinId(item.id); setShowFullForm(true);
                      }} className="text-brand-placeholder hover:text-brand-green p-1" title="Editar" data-testid={`checkin-edit-${item.id}`}><PencilSimple size={12} /></button>
                    )}
                    <button onClick={async () => {
                      const ok = await confirmDelete({ description: `Vai apagar o check-in de ${item.date || ''}.` });
                      if (!ok) return;
                      try {
                        const path = item._type === 'contacto' ? `checkin-contacts/${item.id}` : `checkins/${item.id}`;
                        await axios.delete(`${API}/api/patients/${patientId}/${path}`);
                        toast.success('Check-in eliminado'); fetchAll();
                      } catch { toast.error('Erro'); }
                    }} className="text-brand-placeholder hover:text-red-500 p-1" title="Apagar" data-testid={`checkin-delete-${item.id}`}><Trash size={12} /></button>
                  </div>
                </div>
                {item._type === 'contacto' ? (
                  <div>
                    {item.resultado && <p className="text-xs text-brand-text">{item.resultado}</p>}
                    {item.notes && <Md as="p" className="text-[10px] text-brand-muted mt-1">{item.notes}</Md>}
                    {item.time && <p className="text-[9px] text-brand-placeholder mt-1">{item.time}</p>}
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-3 gap-3 mb-2">
                      <div className="text-center"><p className="text-lg font-heading font-medium text-brand-green">{item.cumprimento}</p><p className="text-[10px] text-brand-muted">Cumprim.</p></div>
                      <div className="text-center"><p className="text-lg font-heading font-medium text-brand-gold">{item.energia}</p><p className="text-[10px] text-brand-muted">Energia</p></div>
                      <div className="text-center"><p className="text-lg font-heading font-medium text-brand-mint-hover">{item.fome}</p><p className="text-[10px] text-brand-muted">Fome</p></div>
                    </div>
                    {item.sintomas && <p className="text-xs text-brand-muted"><strong>Sintomas:</strong> {item.sintomas}</p>}
                    {item.extra_data && Object.keys(item.extra_data).length > 0 && (
                      <details className="mt-2 pt-2 border-t border-brand-border/30">
                        <summary className="text-[10px] text-brand-muted cursor-pointer">Dados adicionais</summary>
                        <div className="grid grid-cols-2 gap-1 mt-1">
                          {Object.entries(item.extra_data).map(([k, v]) => (
                            <div key={k} className="text-xs"><span className="text-brand-muted">{k.replace(/_/g, ' ')}:</span> <span className="text-brand-text font-medium">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span></div>
                          ))}
                        </div>
                      </details>
                    )}
                    {item.nutri_notes && <Md as="p" className="text-[10px] text-amber-700 mt-2 italic">{item.nutri_notes}</Md>}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Full check-in form dialog */}
      <Dialog open={showFullForm} onOpenChange={setShowFullForm}>
        <DialogContent className="rounded-3xl border-brand-border max-h-[85vh] overflow-y-auto max-w-lg">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Registar check-in em nome da paciente</DialogTitle></DialogHeader>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3">
            <p className="text-[11px] text-amber-700 font-medium">A registar check-in em nome da paciente</p>
          </div>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Canal de recolha *</Label>
              <div className="flex flex-wrap gap-1.5">
                {CANAIS.map(c => <button key={c} onClick={() => setCanal(c)} className={`px-2.5 py-1.5 rounded-full text-[10px] font-medium ${canal === c ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>{c}</button>)}
              </div>
            </div>
            {fields.map(f => (
              <div key={f.key}>
                <Label className="text-xs text-brand-muted mb-1 block">{f.label}</Label>
                {f.type === 'scale' && (
                  <div className="flex gap-1.5">
                    {[1,2,3,4,5].map(n => <button key={n} onClick={() => setFormData(p => ({ ...p, [f.key]: n }))} className={`w-9 h-9 rounded-xl text-xs font-semibold ${formData[f.key] === n ? 'bg-brand-green text-white' : 'bg-white border border-brand-border text-brand-muted'}`}>{n}</button>)}
                  </div>
                )}
                {f.type === 'number' && <Input type="number" step="0.1" value={formData[f.key] || ''} onChange={e => setFormData(p => ({ ...p, [f.key]: e.target.value }))} className="rounded-xl border-brand-border w-24" />}
                {f.type === 'choice' && (
                  <div className="flex flex-wrap gap-1">
                    {f.options.map(o => <button key={o} onClick={() => setFormData(p => ({ ...p, [f.key]: o }))} className={`px-2 py-1.5 rounded-xl text-[10px] ${formData[f.key] === o ? 'bg-brand-green text-white' : 'bg-white border border-brand-border text-brand-muted'}`}>{o}</button>)}
                  </div>
                )}
                {f.type === 'text' && <Textarea value={formData[f.key] || ''} onChange={e => setFormData(p => ({ ...p, [f.key]: e.target.value }))} className="rounded-xl border-brand-border text-xs" rows={2} />}
              </div>
            ))}
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Notas da nutri (opcional)</Label>
              <Textarea value={nutriNotes} onChange={e => setNutriNotes(e.target.value)} className="rounded-xl border-brand-border text-xs" rows={2} placeholder="Notas internas..." />
            </div>
            <button onClick={saveFullCheckin} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="save-nutri-checkin">Registar check-in</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Simplified contact dialog */}
      <Dialog open={showContactForm} onOpenChange={setShowContactForm}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Marcar como contactada</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs text-brand-muted mb-1 block">Data</Label><Input type="date" value={contactForm.date} onChange={e => setContactForm(p => ({ ...p, date: e.target.value }))} className="rounded-xl border-brand-border" /></div>
              <div><Label className="text-xs text-brand-muted mb-1 block">Hora</Label><Input type="time" value={contactForm.time} onChange={e => setContactForm(p => ({ ...p, time: e.target.value }))} className="rounded-xl border-brand-border" /></div>
            </div>
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Canal *</Label>
              <div className="flex flex-wrap gap-1.5">
                {CANAIS.map(c => <button key={c} onClick={() => setContactForm(p => ({ ...p, canal: c }))} className={`px-2.5 py-1.5 rounded-full text-[10px] font-medium ${contactForm.canal === c ? 'bg-blue-500 text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>{c}</button>)}
              </div>
            </div>
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Resultado *</Label>
              <div className="flex flex-wrap gap-1.5">
                {RESULTADOS.map(r => <button key={r} onClick={() => setContactForm(p => ({ ...p, resultado: r }))} className={`px-2.5 py-1.5 rounded-full text-[10px] font-medium ${contactForm.resultado === r ? 'bg-blue-500 text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>{r}</button>)}
              </div>
            </div>
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Notas (opcional)</Label>
              <Textarea value={contactForm.notes} onChange={e => setContactForm(p => ({ ...p, notes: e.target.value }))} className="rounded-xl border-brand-border text-xs" rows={2} placeholder="Ex: Paciente disse que está tudo bem..." />
            </div>
            <button onClick={saveContact} className="w-full bg-blue-500 text-white rounded-full py-3 font-medium" data-testid="save-nutri-contact">Registar contacto</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── ANALISES TAB ───
function AnálisesTab({ patientId }) {
  const confirmDelete = useConfirmDelete();
  const confirmClear = useConfirmClear();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [originalNotes, setOriginalNotes] = useState('');
  const [form, setForm] = useState({ date: new Date().toISOString().split('T')[0], parameters: [{ name: '', value: '', unit: '', reference: '', category: '' }], notes: '', pdf_path: null, file_name: null });
  const [uploading, setUploading] = useState(false);
  const [selectedParam, setSelectedParam] = useState(null);
  const [labRequests, setLabRequests] = useState([]);
  const [selectedRequestId, setSelectedRequestId] = useState('none');

  const fetch = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/patients/${patientId}/lab-results`, { withCredentials: true }); setItems(data); } catch { }
  }, [patientId]);
  useEffect(() => { fetch(); }, [fetch]);

  const fetchRequests = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/patients/${patientId}/lab-requests`, { withCredentials: true }); setLabRequests(Array.isArray(data) ? data : []); } catch { }
  }, [patientId]);
  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const addParam = () => setForm({ ...form, parameters: [...form.parameters, { name: '', value: '', unit: '', reference: '', category: '' }] });
  const updateParam = (i, f, v) => { const p = [...form.parameters]; p[i] = { ...p[i], [f]: v }; setForm({ ...form, parameters: p }); };
  const removeParam = (i) => setForm({ ...form, parameters: form.parameters.filter((_, idx) => idx !== i) });

  // Navegação rápida pelo teclado: Enter avança para o campo seguinte (Tab já avança nativamente)
  const focusNextParamInput = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const fields = Array.from(document.querySelectorAll('[data-param-field="true"]'));
    const idx = fields.indexOf(e.target);
    if (idx >= 0 && idx < fields.length - 1) fields[idx + 1].focus();
  };

  const buildParamsFromRequest = (reqId) => {
    const req = labRequests.find(r => r.id === reqId);
    if (!req) return [{ name: '', value: '', unit: '', reference: '', category: '' }];
    const params = (req.selected_exams || []).map(name => ({ name, value: '', unit: '', reference: '', category: '' }));
    if (req.outros_text) params.push({ name: req.outros_text, value: '', unit: '', reference: '', category: '' });
    return params.length ? params : [{ name: '', value: '', unit: '', reference: '', category: '' }];
  };

  const requestLabel = (req) => {
    const d = req.created_at ? fmtDay(req.created_at) : '';
    return req.doctor_name ? `Pedido de ${d} (Dr(a). ${req.doctor_name})` : `Pedido de ${d}`;
  };

  const openNewForm = () => {
    setEditId(null);
    const mostRecentId = labRequests[0]?.id || 'none';
    setSelectedRequestId(mostRecentId);
    setForm({ date: new Date().toISOString().split('T')[0], parameters: buildParamsFromRequest(mostRecentId), notes: '', pdf_path: null, file_name: null });
    setOriginalNotes('');
    setShowForm(true);
  };

  const openEditForm = (r) => {
    setEditId(r.id);
    setSelectedRequestId('none');
    setForm({ date: r.date, parameters: r.parameters?.length ? r.parameters : [{ name: '', value: '', unit: '', reference: '', category: '' }], notes: r.notes || '', pdf_path: r.pdf_path, file_name: r.file_name });
    setOriginalNotes(r.notes || '');
    setShowForm(true);
  };

  const handleRequestChange = (reqId) => {
    setSelectedRequestId(reqId);
    if (editId) {
      // modo edição: nunca substitui parâmetros já preenchidos, só acrescenta os que
      // ainda faltam do pedido escolhido
      const newParams = buildParamsFromRequest(reqId).filter(np => np.name.trim());
      setForm(prev => {
        const existingNames = new Set(prev.parameters.map(p => p.name.trim().toLowerCase()).filter(Boolean));
        const toAdd = newParams.filter(np => !existingNames.has(np.name.trim().toLowerCase()));
        return { ...prev, parameters: [...prev.parameters.filter(p => p.name.trim()), ...toAdd] };
      });
    } else {
      setForm(prev => ({ ...prev, parameters: buildParamsFromRequest(reqId) }));
    }
  };

  const save = async () => {
    if (editId && (originalNotes || '').trim() && !(form.notes || '').trim()) {
      const ok = await confirmClear('Notas da análise');
      if (!ok) return;
    }
    const payload = { ...form, parameters: form.parameters.filter(p => p.name.trim() && p.value.toString().trim()) };
    try {
      if (editId) { await axios.put(`${API}/api/lab-results/${editId}`, payload, { withCredentials: true }); }
      else { await axios.post(`${API}/api/patients/${patientId}/lab-results`, payload, { withCredentials: true }); }
      toast.success('Análise guardada'); setShowForm(false); setEditId(null); fetch();
    } catch { toast.error('Erro'); }
  };

  const del = async (id) => {
    const it = items.find(x => x.id === id);
    const ok = await confirmDelete({ description: `Vai apagar a análise laboratorial de ${it?.date || ''}.` });
    if (!ok) return;
    await axios.delete(`${API}/api/lab-results/${id}`); toast.success('Eliminada'); fetch();
  };

  const handlePdfUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      await axios.post(`${API}/api/patients/${patientId}/lab-results/upload-pdf`, fd);
      toast.success('PDF guardado na ficha. Introduz os valores manualmente quando quiseres (botão de editar).');
      fetch();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Erro ao carregar o PDF';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    }
    finally { setUploading(false); }
  };

  const isOutOfRange = (p) => {
    if (!p.reference || !p.value) return false;
    const valStr = String(p.value).replace(',', '.');
    const val = parseFloat(valStr);
    if (isNaN(val)) return false;
    const ref = String(p.reference).replace(/,/g, '.');
    // Pattern: min - max (e.g. "12.0 - 16.0" or "70-100")
    const rangeMatch = ref.match(/([\d.]+)\s*[-–]\s*([\d.]+)/);
    if (rangeMatch) {
      const low = parseFloat(rangeMatch[1]);
      const high = parseFloat(rangeMatch[2]);
      if (!isNaN(low) && !isNaN(high)) return val < low || val > high;
    }
    // Pattern: > value (e.g. "> 60")
    const gtMatch = ref.match(/>\s*([\d.]+)/);
    if (gtMatch) { const threshold = parseFloat(gtMatch[1]); if (!isNaN(threshold)) return val <= threshold; }
    // Pattern: < value (e.g. "< 200")
    const ltMatch = ref.match(/<\s*([\d.]+)/);
    if (ltMatch) { const threshold = parseFloat(ltMatch[1]); if (!isNaN(threshold)) return val >= threshold; }
    return false;
  };

  const CAT_COLORS = { hemograma: 'bg-red-50 text-red-700', bioquímica: 'bg-blue-50 text-blue-700', hormonas: 'bg-purple-50 text-purple-700', vitaminas: 'bg-amber-50 text-amber-700', tiróide: 'bg-teal-50 text-teal-700', ferro: 'bg-orange-50 text-orange-700', lípidos: 'bg-yellow-50 text-yellow-700', urina: 'bg-cyan-50 text-cyan-700' };
  const catColor = (cat) => CAT_COLORS[(cat || '').toLowerCase()] || 'bg-brand-bg text-brand-muted';

  // Evolution: group all params by name across all results
  const allParamNames = new Set();
  items.forEach(r => (r.parameters || []).forEach(p => { if (p.name) allParamNames.add(p.name); }));

  const getParamHistory = (paramName) => {
    const history = [];
    items.forEach(r => {
      const p = (r.parameters || []).find(x => x.name === paramName);
      if (p && p.value) {
        const numVal = parseFloat(p.value);
        if (!isNaN(numVal)) history.push({ date: r.date, value: numVal, unit: p.unit, reference: p.reference });
      }
    });
    return history.reverse();
  };

  const patientUploadedCount = items.filter(r => r.source === 'patient').length;

  return (
    <div data-testid="tab-analises">
      {patientUploadedCount > 0 && (
        <div className="mb-3">
          <span className="text-[11px] font-medium px-3 py-1.5 rounded-full bg-brand-green/10 text-brand-green" data-testid="lab-results-patient-count-badge">{patientUploadedCount} resultado{patientUploadedCount > 1 ? 's' : ''} enviado{patientUploadedCount > 1 ? 's' : ''} pela paciente</span>
        </div>
      )}
      <div className="bg-brand-bg/50 border border-brand-border rounded-xl px-3 py-2 mb-3 text-[11px] text-brand-muted" data-testid="lab-ai-disabled-notice">
        Por exigência de RGPD, os PDFs de análises já não são lidos automaticamente por IA. O ficheiro fica guardado para consulta e os valores são sempre introduzidos manualmente.
      </div>
      <div className="flex justify-end mb-4 gap-2 flex-wrap">
        <label className={`border border-brand-border text-brand-green hover:bg-brand-green/5 transition-all rounded-full px-3 py-2 text-sm font-medium flex items-center gap-1 cursor-pointer ${uploading ? 'opacity-50' : ''}`} data-testid="upload-lab-pdf-btn">
          <UploadSimple size={14} /> {uploading ? 'A carregar...' : 'Importar PDF'}
          <input type="file" accept=".pdf" onChange={e => handlePdfUpload(e.target.files?.[0])} className="hidden" disabled={uploading} />
        </label>
        <button onClick={openNewForm} className="bg-brand-green text-white rounded-full px-4 py-2 text-sm font-medium flex items-center gap-1" data-testid="add-lab-result-btn"><Plus size={14} /> Manual</button>
      </div>

      {/* Results list */}
      {items.length === 0 ? <p className="text-center text-brand-muted py-8">Sem análises registadas.</p> : (
        <div className="space-y-3">
          {items.map(r => {
            const grouped = {};
            (r.parameters || []).forEach(p => { const c = p.category || 'outro'; if (!grouped[c]) grouped[c] = []; grouped[c].push(p); });
            return (
              <div key={r.id} className="bg-white border border-brand-border rounded-2xl p-4" data-testid={`lab-result-${r.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-brand-green bg-brand-mint/20 px-2 py-0.5 rounded-full">{fmtDatePt(r.date)}</span>
                    {r.source === 'patient' && <span className="text-[9px] font-medium px-2 py-0.5 rounded-full bg-brand-green/10 text-brand-green" data-testid={`lab-result-patient-badge-${r.id}`}>Enviado pela paciente</span>}
                    {r.file_name && <span className="text-[9px] text-brand-muted flex items-center gap-0.5"><UploadSimple size={10} /> {r.file_name}</span>}
                  </div>
                  <div className="flex gap-1">
                    {r.pdf_path && <a href={`${API}/api/files/${r.pdf_path}`} target="_blank" rel="noreferrer" className="p-1 text-brand-placeholder hover:text-brand-green" title="Ver PDF"><UploadSimple size={14} /></a>}
                    {r.file_path && <a href={`${API}/api/files/${r.file_path}`} target="_blank" rel="noreferrer" className="p-1 text-brand-placeholder hover:text-brand-green" title="Ver ficheiro"><UploadSimple size={14} /></a>}
                    <button onClick={() => openEditForm(r)} className="p-1 text-brand-placeholder hover:text-brand-green"><PencilSimple size={14} /></button>
                    <button onClick={() => del(r.id)} className="p-1 text-brand-placeholder hover:text-red-500"><Trash size={14} /></button>
                  </div>
                </div>
                {Object.entries(grouped).map(([cat, params]) => (
                  <div key={cat} className="mb-2">
                    <span className={`text-[8px] px-2 py-0.5 rounded-full font-medium uppercase ${catColor(cat)}`}>{cat}</span>
                    <div className="mt-1 space-y-0.5">
                      {params.map((p, i) => {
                        const oor = isOutOfRange(p);
                        return (
                          <div key={i} className={`flex items-center gap-2 text-xs px-2 py-0.5 rounded ${oor ? 'bg-red-50' : ''}`}>
                            <span className="font-medium text-brand-text flex-1">{p.name}</span>
                            <span className={`font-semibold ${oor ? 'text-red-600' : 'text-brand-green'}`}>{p.value} {p.unit}</span>
                            {p.reference && <span className="text-brand-muted text-[10px]">({p.reference})</span>}
                            {oor && <span className="text-[8px] text-red-600 font-semibold">FORA</span>}
                            <button onClick={() => setSelectedParam(selectedParam === p.name ? null : p.name)} className="text-brand-placeholder hover:text-brand-green text-[9px]">Evolução</button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {r.notes && <Md as="p" className="text-xs text-brand-muted mt-2 italic">{r.notes}</Md>}
              </div>
            );
          })}
        </div>
      )}

      {/* Parameter evolution mini-chart */}
      {selectedParam && (() => {
        const history = getParamHistory(selectedParam);
        if (history.length < 2) return <p className="text-xs text-brand-muted text-center py-2 mt-2">Necessários pelo menos 2 registos para evolução.</p>;
        const maxVal = Math.max(...history.map(h => h.value));
        const minVal = Math.min(...history.map(h => h.value));
        const range = maxVal - minVal || 1;
        return (
          <div className="bg-white border border-brand-border rounded-2xl p-4 mt-3" data-testid="param-evolution">
            <p className="text-xs font-medium text-brand-text mb-2">Evolução: {selectedParam}</p>
            <div className="flex items-end gap-1 h-24">
              {history.map((h, i) => (
                <div key={i} className="flex-1 flex flex-col items-center">
                  <span className="text-[8px] text-brand-green font-medium mb-0.5">{h.value}</span>
                  <div className="w-full bg-brand-green/20 rounded-t" style={{ height: `${Math.max(8, ((h.value - minVal) / range) * 80)}%` }}>
                    <div className="w-full h-full bg-brand-green/60 rounded-t" />
                  </div>
                  <span className="text-[7px] text-brand-muted mt-0.5">{h.date.slice(5)}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Manual entry / edit dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="rounded-3xl border-brand-border max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">{editId ? 'Editar análise' : 'Nova análise'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {labRequests.length > 0 && (
              <div>
                <Label className="text-xs text-brand-muted mb-1 block">Basear em pedido de exames</Label>
                <Select value={selectedRequestId} onValueChange={handleRequestChange}>
                  <SelectTrigger className="rounded-xl border-brand-border text-xs" data-testid="lab-request-select"><SelectValue placeholder="Escolher pedido" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem pedido, começar em branco</SelectItem>
                    {labRequests.map(req => (
                      <SelectItem key={req.id} value={req.id} data-testid={`lab-request-option-${req.id}`}>{requestLabel(req)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {(form.pdf_path || form.file_name) && (
              <div className="text-[11px] text-brand-muted flex items-center gap-1" data-testid="lab-form-linked-pdf"><UploadSimple size={12} /> Ligado ao ficheiro: {form.file_name}</div>
            )}
            <div><Label className="text-xs text-brand-muted mb-1 block">Data da colheita</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="rounded-xl border-brand-border" data-testid="lab-date" /></div>
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Parâmetros</Label>
              {form.parameters.map((p, i) => (
                <div key={i} className="flex gap-1.5 mb-2">
                  <Input value={p.name} onChange={e => updateParam(i, 'name', e.target.value)} onKeyDown={focusNextParamInput} data-param-field="true" placeholder="Parâmetro" className="flex-1 rounded-xl border-brand-border text-xs" />
                  <Input value={p.value} onChange={e => updateParam(i, 'value', e.target.value)} onKeyDown={focusNextParamInput} data-param-field="true" placeholder="Valor" className="w-20 rounded-xl border-brand-border text-xs" />
                  <Input value={p.unit} onChange={e => updateParam(i, 'unit', e.target.value)} onKeyDown={focusNextParamInput} data-param-field="true" placeholder="Un." className="w-16 rounded-xl border-brand-border text-xs" />
                  <Input value={p.reference} onChange={e => updateParam(i, 'reference', e.target.value)} onKeyDown={focusNextParamInput} data-param-field="true" placeholder="Ref." className="w-20 rounded-xl border-brand-border text-xs" />
                  {form.parameters.length > 1 && <button onClick={() => removeParam(i)} className="text-red-400 p-1"><Trash size={12} /></button>}
                </div>
              ))}
              <button onClick={addParam} className="text-xs text-brand-green flex items-center gap-1"><Plus size={12} /> Adicionar parâmetro</button>
            </div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Notas</Label><Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="rounded-xl border-brand-border" rows={2} /></div>
            <button onClick={save} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="save-lab-result-btn">Guardar</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── AVALIACAO FISICA TAB ───

// ─── PLANOS TAB (inline editing) ───
// ─── CLINICO TAB (blood pressure + glucose) ───
function ClinicoTab({ patientId }) {
  const confirmDelete = useConfirmDelete();
  const [records, setRecords] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().split('T')[0], systolic: '', diastolic: '', glucose_mg_dl: '', meal_moment: '', notes: '' });
  const fetchRec = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/patients/${patientId}/clinical-records`); setRecords(Array.isArray(data) ? data : []); } catch {}
  }, [patientId]);
  useEffect(() => { fetchRec(); }, [fetchRec]);

  const save = async () => {
    const payload = {
      date: form.date,
      systolic: form.systolic ? parseInt(form.systolic) : null,
      diastolic: form.diastolic ? parseInt(form.diastolic) : null,
      glucose_mg_dl: form.glucose_mg_dl ? parseFloat(form.glucose_mg_dl) : null,
      meal_moment: form.meal_moment || null,
      notes: form.notes || '',
    };
    if (payload.systolic === null && payload.diastolic === null && payload.glucose_mg_dl === null) {
      toast.error('Preenche pelo menos um valor');
      return;
    }
    try {
      await axios.post(`${API}/api/patients/${patientId}/clinical-records`, payload);
      toast.success('Registo guardado'); setShowAdd(false);
      setForm({ date: new Date().toISOString().split('T')[0], systolic: '', diastolic: '', glucose_mg_dl: '', meal_moment: '', notes: '' });
      fetchRec();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };
  const remove = async (id) => {
    const rec = records.find(r => r.id === id);
    const ok = await confirmDelete({ description: `Vai apagar o registo clínico de ${rec?.date || ''}.` });
    if (!ok) return;
    try { await axios.delete(`${API}/api/clinical-records/${id}`); toast.success('Eliminado'); fetchRec(); } catch { toast.error('Erro'); }
  };

  const chartData = [...records].slice(0, 30).reverse().map(r => ({
    date: (r.date || '').slice(5),
    sys: r.systolic ?? null,
    dia: r.diastolic ?? null,
    glu: r.glucose_mg_dl ?? null,
  }));
  const hasBP = chartData.some(p => p.sys != null || p.dia != null);
  const hasGlu = chartData.some(p => p.glu != null);
  const severeRecords = records.filter(r => r.severe);

  return (
    <div data-testid="tab-clinico" className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="font-heading text-lg text-brand-text">Registos clínicos</h3>
          <p className="text-[11px] text-brand-muted">Tensão arterial (mmHg) e glicemia (mg/dL). Relevante para grávidas e patologia.</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="bg-brand-green text-white rounded-full px-3 py-1.5 text-xs font-medium flex items-center gap-1" data-testid="clinico-add-btn"><Plus size={12} /> Novo</button>
      </div>

      {severeRecords.length > 0 && (
        <div className="bg-red-50 border border-red-300 rounded-2xl p-3 flex items-start gap-2" data-testid="clinico-severe-alert">
          <Warning size={18} weight="fill" className="text-red-500 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-xs font-semibold text-red-700">{severeRecords.length} registo{severeRecords.length > 1 ? 's' : ''} com valor{severeRecords.length > 1 ? 'es' : ''} fora do limite clínico</p>
            <p className="text-[10px] text-red-600">{(severeRecords[0].alerts || []).join('; ')} (último em {fmtDatePt(severeRecords[0].date)}). Já foi enviado alerta clínico.</p>
          </div>
        </div>
      )}

      {records.length === 0 ? (
        <div className="bg-white border border-brand-border rounded-2xl p-6 text-center text-xs text-brand-muted" data-testid="clinico-empty">Ainda sem registos.</div>
      ) : (
        <>
          {(hasBP || hasGlu) && (
            <div className="bg-white border border-brand-border rounded-2xl p-3" data-testid="clinico-chart">
              <p className="text-[11px] text-brand-muted mb-1">Evolução</p>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={chartData} margin={{ top: 5, right: 10, bottom: 0, left: -20 }}>
                  <CartesianGrid stroke="#E8E5DE" strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 9 }} />
                  <YAxis yAxisId="l" domain={[40, 200]} tick={{ fontSize: 9 }} />
                  <YAxis yAxisId="r" orientation="right" domain={[40, 250]} tick={{ fontSize: 9 }} />
                  <RTooltip contentStyle={{ fontSize: 11 }} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  {hasBP && <Line yAxisId="l" type="monotone" dataKey="sys" stroke="#657161" strokeWidth={2} name="Sistólica" connectNulls dot={{ r: 2 }} />}
                  {hasBP && <Line yAxisId="l" type="monotone" dataKey="dia" stroke="#BBD6D5" strokeWidth={2} name="Diastólica" connectNulls dot={{ r: 2 }} />}
                  {hasGlu && <Line yAxisId="r" type="monotone" dataKey="glu" stroke="#D7BD7B" strokeWidth={2} name="Glicemia" connectNulls dot={{ r: 2 }} />}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          {hasGlu && (() => {
            // 7-week histogram of glycemia: average per week, color by worst DGS zone
            const ZONES = {jejum:{green:95,amber:105},pos_prandial_1h:{green:140,amber:160},pos_prandial_2h:{green:120,amber:140}};
            const computeZone = (glu, m) => {
              const t = ZONES[m]; if (!t) return null;
              return glu < t.green ? 'green' : (glu < t.amber ? 'amber' : 'red');
            };
            const weekKey = (d) => {
              const dt = new Date(d + 'T00:00:00');
              const onejan = new Date(dt.getFullYear(),0,1);
              const days = Math.floor((dt - onejan) / 86400000);
              const week = Math.ceil((days + onejan.getDay() + 1) / 7);
              return `${dt.getFullYear()}-W${String(week).padStart(2,'0')}`;
            };
            const buckets = {};
            records.filter(r => r.glucose_mg_dl != null).forEach(r => {
              const k = weekKey(r.date);
              if (!buckets[k]) buckets[k] = { week: k, values: [], zones: [], dateRef: r.date };
              buckets[k].values.push(r.glucose_mg_dl);
              const z = r.dgs_zone || computeZone(r.glucose_mg_dl, r.meal_moment);
              if (z) buckets[k].zones.push(z);
            });
            const sortedKeys = Object.keys(buckets).sort();
            const last7 = sortedKeys.slice(-7);
            const data = last7.map(k => {
              const b = buckets[k];
              const avg = Math.round(b.values.reduce((a,c)=>a+c,0) / b.values.length);
              const worst = b.zones.includes('red') ? 'red' : b.zones.includes('amber') ? 'amber' : (b.zones.includes('green') ? 'green' : 'grey');
              const wk = k.split('-W')[1];
              return { label: `S${wk}`, avg, worst, count: b.values.length };
            });
            if (data.length === 0) return null;
            const colorMap = { green: '#86C7A1', amber: '#E6B96A', red: '#E26B6B', grey: '#BDBDBD' };
            return (
              <div className="bg-white border border-brand-border rounded-2xl p-3" data-testid="clinico-glycemia-histogram">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[11px] text-brand-muted">Glicemia, últimas 7 semanas (média por semana, cor = pior zona DGS)</p>
                  <div className="flex items-center gap-2 text-[9px]">
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{background:'#86C7A1'}}/>verde</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{background:'#E6B96A'}}/>limite</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{background:'#E26B6B'}}/>fora</span>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={data} margin={{ top: 5, right: 10, bottom: 0, left: -20 }}>
                    <CartesianGrid stroke="#E8E5DE" strokeDasharray="3 3" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                    <YAxis domain={[0, 'auto']} tick={{ fontSize: 9 }} />
                    <RTooltip contentStyle={{ fontSize: 11 }} formatter={(v, _n, p) => [`${v} mg/dL (n=${p.payload.count})`, 'Média']} />
                    <Bar dataKey="avg" radius={[6,6,0,0]}>
                      {data.map((d, i) => <Cell key={i} fill={colorMap[d.worst]} data-testid={`glu-hist-bar-${d.worst}`} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
          );})()}
          <ul className="space-y-1.5">
            {records.map(r => (
              <li key={r.id} className={`bg-white border rounded-2xl p-3 ${r.severe ? 'border-red-300' : 'border-brand-border'}`} data-testid={`clinico-row-${r.id}`}>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium text-brand-text flex items-center gap-1.5">
                    {r.severe && <Warning size={12} weight="fill" className="text-red-500" />}
                    {r.date}
                  </p>
                  <div className="flex items-center gap-2">
                    {r.self_reported && <span className="text-[9px] bg-brand-gold/15 text-brand-gold-hover px-1.5 py-0.5 rounded-full">paciente</span>}
                    <button onClick={() => remove(r.id)} className="text-brand-placeholder hover:text-red-500"><Trash size={12} /></button>
                  </div>
                </div>
                <div className="flex gap-3 mt-1 text-[11px] text-brand-text flex-wrap">
                  {(r.systolic != null || r.diastolic != null) && <span>TA: <strong>{r.systolic ?? '-'}/{r.diastolic ?? '-'}</strong> mmHg</span>}
                  {r.glucose_mg_dl != null && (() => {
                    const zones = {jejum:{green:95,amber:105,label:'Jejum'},pos_prandial_1h:{green:140,amber:160,label:'1h pós-prandial'},pos_prandial_2h:{green:120,amber:140,label:'2h pós-prandial'},casual:{label:'Casual'}};
                    const t = zones[r.meal_moment];
                    let zone = r.dgs_zone;
                    if (!zone && t && t.green) zone = r.glucose_mg_dl < t.green ? 'green' : (r.glucose_mg_dl < t.amber ? 'amber' : 'red');
                    const cls = zone === 'green' ? 'bg-green-100 text-green-700 border-green-300' : zone === 'amber' ? 'bg-amber-100 text-amber-700 border-amber-300' : zone === 'red' ? 'bg-red-100 text-red-700 border-red-300' : 'bg-gray-100 text-gray-600 border-gray-300';
                    const momentLabel = t?.label || 'momento não registado';
                    return <span className={`px-2 py-0.5 rounded-full border ${cls}`} data-testid={`glu-zone-admin-${zone || 'grey'}`}>Glicemia: <strong>{r.glucose_mg_dl}</strong> mg/dL · <span className="opacity-80">{momentLabel}</span></span>;
                  })()}
                </div>
                {r.notes && <p className="text-[10px] text-brand-muted italic mt-1">"{r.notes}"</p>}
              </li>
            ))}
          </ul>
        </>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="rounded-3xl border-brand-border" data-testid="clinico-add-dialog">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Novo registo clínico</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Data</Label>
              <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="rounded-xl border-brand-border" data-testid="clinico-form-date" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-brand-muted mb-1 block">Sistólica (mmHg)</Label>
                <Input type="number" min="50" max="260" value={form.systolic} onChange={e => setForm({ ...form, systolic: e.target.value })} className="rounded-xl border-brand-border" placeholder="120" data-testid="clinico-form-sys" />
              </div>
              <div>
                <Label className="text-xs text-brand-muted mb-1 block">Diastólica (mmHg)</Label>
                <Input type="number" min="30" max="160" value={form.diastolic} onChange={e => setForm({ ...form, diastolic: e.target.value })} className="rounded-xl border-brand-border" placeholder="80" data-testid="clinico-form-dia" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Glicemia (mg/dL)</Label>
              <Input type="number" step="0.1" min="30" max="600" value={form.glucose_mg_dl} onChange={e => setForm({ ...form, glucose_mg_dl: e.target.value })} className="rounded-xl border-brand-border" placeholder="95" data-testid="clinico-form-glu" />
            </div>
            {form.glucose_mg_dl && (
              <div>
                <Label className="text-xs text-brand-muted mb-1 block">Momento da medição</Label>
                <select value={form.meal_moment} onChange={e => setForm({ ...form, meal_moment: e.target.value })} className="w-full border border-brand-border rounded-xl px-3 py-2 text-sm bg-white" data-testid="clinico-form-moment">
                  <option value="">Não especificado</option>
                  <option value="jejum">Jejum (alvo: &lt; 95)</option>
                  <option value="pos_prandial_1h">1h pós-prandial (alvo: &lt; 140)</option>
                  <option value="pos_prandial_2h">2h pós-prandial (alvo: &lt; 120)</option>
                  <option value="casual">Casual / outro</option>
                </select>
              </div>
            )}
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Notas</Label>
              <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="rounded-xl border-brand-border text-sm" placeholder="Em jejum, pós-prandial, etc." data-testid="clinico-form-notes" />
            </div>
            <button onClick={save} className="w-full bg-brand-green text-white rounded-full py-2.5 text-sm font-medium" data-testid="clinico-form-save">Guardar</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}



// ─── EVOLUÇÃO TAB (weight records + goals) ───
function EvolucaoTab({ patientId }) {
  const confirmDelete = useConfirmDelete();
  const confirmClear = useConfirmClear();
  const [records, setRecords] = useState([]);
  const [goals, setGoals] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().split('T')[0], peso: '', altura: '', gordura_pct: '', massa_muscular: '', massa_gorda: '', massa_ossea: '', agua_corporal: '', gordura_visceral: '', imc: '', tmb: '' });
  const [goalForm, setGoalForm] = useState({ peso_objetivo: '', foco: '' });
  const [showGoals, setShowGoals] = useState(false);
  const [expandedRecord, setExpandedRecord] = useState(null);
  const fetch = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/patients/${patientId}/weight-records`); setRecords(data); } catch {}
  }, [patientId]);
  const fetchGoals = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/patients/${patientId}/weight-goals`); setGoals(data); setGoalForm({ peso_objetivo: data.peso_objetivo || '', foco: data.foco || '' }); } catch {}
  }, [patientId]);
  useEffect(() => { fetch(); fetchGoals(); }, [fetch, fetchGoals]);
  const saveRecord = async () => {
    const num = (v) => v !== '' && v != null ? parseFloat(v) : null;
    // Auto-calcula IMC se peso e altura presentes e IMC vazio
    let imc = num(form.imc);
    if (imc == null && form.peso && form.altura) { const h = parseFloat(form.altura) / 100; if (h > 0) imc = Math.round((parseFloat(form.peso) / (h * h)) * 10) / 10; }
    try { await axios.post(`${API}/api/patients/${patientId}/weight-records`, { ...form, peso: parseFloat(form.peso), altura: num(form.altura), gordura_pct: num(form.gordura_pct), massa_muscular: num(form.massa_muscular), massa_gorda: num(form.massa_gorda), massa_ossea: num(form.massa_ossea), agua_corporal: num(form.agua_corporal), gordura_visceral: num(form.gordura_visceral), imc, tmb: num(form.tmb), source: 'prozis' }); toast.success('Registo guardado'); setShowAdd(false); fetch(); } catch { toast.error('Erro'); }
  };
  const saveGoals = async () => {
    const was = (goals.foco || '').trim();
    const now = (goalForm.foco || '').trim();
    if (was && !now) {
      const ok = await confirmClear('Foco');
      if (!ok) return;
    }
    try { await axios.put(`${API}/api/patients/${patientId}/weight-goals`, { peso_objetivo: goalForm.peso_objetivo ? parseFloat(goalForm.peso_objetivo) : null, foco: goalForm.foco }); toast.success('Objetivos guardados'); setShowGoals(false); fetchGoals(); } catch { toast.error('Erro'); }
  };
  const handleImport = async (file) => {
    if (!file) return;
    try { const fd = new FormData(); fd.append('file', file); const { data } = await axios.post(`${API}/api/patients/${patientId}/weight-records/import-inbody`, fd); toast.success(`${data.records?.length || 0} registos importados`); fetch(); } catch { toast.error('Erro na importação'); }
  };
  const deleteRecord = async (id) => {
    const rec = records.find(r => r.id === id);
    const ok = await confirmDelete({ description: `Vai apagar o registo de evolução de peso de ${fmtDate(rec?.date)}${rec?.peso ? ` (${fmtNum(rec.peso)} kg)` : ''}.` });
    if (!ok) return;
    try { await axios.delete(`${API}/api/weight-records/${id}`); toast.success('Registo eliminado'); fetch(); } catch { toast.error('Erro'); }
  };
  const latest = records[0];
  const chartData = records.slice(0, 12).reverse();
  return (
    <div data-testid="tab-evolucao">
      <div className="flex justify-end mb-4 gap-2">
        <button onClick={() => setShowGoals(true)} className="border border-brand-border text-brand-green rounded-full px-3 py-1.5 text-xs font-medium" data-testid="set-goals-btn">Objetivos</button>
        <label className="border border-brand-border text-brand-muted rounded-full px-3 py-1.5 text-xs font-medium cursor-pointer flex items-center gap-1"><UploadSimple size={12} /> InBody<input type="file" accept=".csv,.xlsx" onChange={e => handleImport(e.target.files?.[0])} className="hidden" /></label>
        <button onClick={() => { setForm({ date: new Date().toISOString().split('T')[0], peso: '', altura: '', gordura_pct: '', massa_muscular: '', massa_gorda: '', massa_ossea: '', agua_corporal: '', gordura_visceral: '', imc: '', tmb: '' }); setShowAdd(true); }} className="bg-brand-green text-white rounded-full px-4 py-1.5 text-xs font-medium flex items-center gap-1" data-testid="add-weight-record"><Plus size={12} /> Novo registo</button>
      </div>
      {/* Summary */}
      {latest && (
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-white border border-brand-border rounded-2xl p-3 text-center"><p className="text-lg font-heading font-semibold text-brand-text" data-testid="weight-card-value">{fmtNum(latest.peso)}</p><p className="text-[10px] text-brand-muted">Peso (kg)</p></div>
          {goals.peso_objetivo && <div className="bg-white border border-brand-border rounded-2xl p-3 text-center"><p className="text-lg font-heading font-semibold text-brand-green">{fmtNum(goals.peso_objetivo)}</p><p className="text-[10px] text-brand-muted">Objetivo (kg)</p></div>}
          {latest.gordura_pct && <div className="bg-white border border-brand-border rounded-2xl p-3 text-center"><p className="text-lg font-heading font-semibold text-brand-gold-hover">{fmtNum(latest.gordura_pct)}%</p><p className="text-[10px] text-brand-muted">Gordura</p></div>}
        </div>
      )}
      {/* Chart */}
      {chartData.length >= 2 && (
        <div className="bg-white border border-brand-border rounded-2xl p-4 mb-4">
          <h4 className="text-xs text-brand-muted uppercase tracking-wider mb-3">Evolução do peso</h4>
          <div className="h-32">
            <svg className="w-full h-full" viewBox={`0 0 ${chartData.length * 50} 130`} preserveAspectRatio="none">
              {(() => { const vals = chartData.map(r => r.peso); const mx = Math.max(...vals, goals.peso_objetivo || 0); const mn = Math.min(...vals, goals.peso_objetivo || 999); const rng = mx - mn || 1; return chartData.map((r, i) => { const x = i * 50 + 25; const y = 120 - ((r.peso - mn) / rng) * 110; const next = chartData[i + 1]; return (<g key={i}>{next && <line x1={x} y1={y} x2={(i + 1) * 50 + 25} y2={120 - ((next.peso - mn) / rng) * 110} stroke="#657161" strokeWidth="2" />}<circle cx={x} cy={y} r="3" fill="#657161" /><text x={x} y={y - 8} textAnchor="middle" fontSize="8" fill="#7A8276">{fmtNum(r.peso)}</text></g>); }); })()}
            </svg>
          </div>
        </div>
      )}
      {/* Weekly averages — Item 4 Fase 5: agrupa pesagens por semana ISO (Seg-Dom), mostra média e variação vs semana anterior */}
      {records.length > 1 && (() => {
        const isoWeek = (d) => { const dt = new Date(d + 'T00:00:00'); const day = dt.getUTCDay() || 7; dt.setUTCDate(dt.getUTCDate() + 4 - day); const ys = new Date(Date.UTC(dt.getUTCFullYear(), 0, 1)); return `${dt.getUTCFullYear()}-W${String(Math.ceil((((dt - ys) / 86400000) + 1) / 7)).padStart(2, '0')}`; };
        const weekRange = (d) => { const dt = new Date(d + 'T00:00:00'); const day = (dt.getUTCDay() + 6) % 7; const mon = new Date(dt); mon.setUTCDate(dt.getUTCDate() - day); const sun = new Date(mon); sun.setUTCDate(mon.getUTCDate() + 6); const fmt = (x) => `${String(x.getUTCDate()).padStart(2, '0')} ${x.toLocaleString('pt-PT', { month: 'short', timeZone: 'UTC' }).replace('.', '')}`; return `${fmt(mon)}–${fmt(sun)}`; };
        const buckets = {};
        records.forEach(r => { const k = isoWeek(r.date); if (!buckets[k]) buckets[k] = { week: k, label: weekRange(r.date), values: [] }; buckets[k].values.push(parseFloat(r.peso) || 0); });
        const sorted = Object.values(buckets).filter(b => b.values.length >= 2).sort((a, b) => a.week.localeCompare(b.week));
        if (sorted.length === 0) return null;
        const withAvg = sorted.map(b => ({ ...b, avg: Math.round(b.values.reduce((a, c) => a + c, 0) / b.values.length * 10) / 10 }));
        return (
          <div className="bg-brand-bg/30 border border-brand-border rounded-2xl p-3 mb-4" data-testid="weekly-averages">
            <h4 className="text-[10px] text-brand-muted uppercase tracking-wider mb-2">Médias semanais</h4>
            <div className="space-y-1">
              {withAvg.slice().reverse().map((b, idx) => {
                const prev = withAvg.slice().reverse()[idx + 1];
                const delta = prev ? Math.round((b.avg - prev.avg) * 10) / 10 : null;
                return (
                  <div key={b.week} className="flex items-center justify-between text-xs" data-testid={`weekly-avg-${b.week}`}>
                    <span className="text-brand-text">Semana {b.label}: <strong>{fmtNum(b.avg, 1)} kg</strong> <span className="text-brand-muted">(média de {b.values.length})</span></span>
                    {delta != null && delta !== 0 && (
                      <span className={`text-[10px] font-medium ${delta < 0 ? 'text-brand-green' : 'text-red-500'}`}>{delta > 0 ? '+' : ''}{fmtNum(delta, 1)} kg face à semana anterior</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
      {/* History - expandable for InBody */}
      <div className="space-y-2">
        {records.map(r => (
          <div key={r.id} className="bg-white border border-brand-border rounded-xl overflow-hidden" data-testid={`weight-${r.id}`}>
            <div className="p-3 flex items-center justify-between group cursor-pointer" onClick={() => setExpandedRecord(expandedRecord === r.id ? null : r.id)}>
              <div><span className="text-xs text-brand-muted">{fmtDate(r.date)}</span>{r.source === 'inbody' && <span className="ml-1 text-[9px] bg-brand-mint/15 text-brand-green px-1.5 py-0.5 rounded-full">InBody</span>}{r.source === 'prozis' && <span className="ml-1 text-[9px] bg-brand-gold/15 text-brand-gold-hover px-1.5 py-0.5 rounded-full">Prozis</span>}</div>
              <div className="flex gap-3 items-center">
                <span className="text-sm font-semibold text-brand-text">{fmtNum(r.peso)} kg</span>
                {r.gordura_pct && <span className="text-xs text-brand-muted">{fmtNum(r.gordura_pct)}%</span>}
                {r.massa_muscular && <span className="text-xs text-brand-muted">{fmtNum(r.massa_muscular)}kg musc.</span>}
                <button onClick={(e) => { e.stopPropagation(); deleteRecord(r.id); }} className="hidden group-hover:block text-brand-placeholder hover:text-red-500 p-1" data-testid={`delete-weight-${r.id}`}><Trash size={13} /></button>
              </div>
            </div>
            {expandedRecord === r.id && (
              <div className="border-t border-brand-border/50">
                {r.inbody_data ? (
                  <InBodyBackoffice data={r.inbody_data} patientName={r.notes?.replace('InBody - ', '') || ''} allRecords={records.filter(x => x.inbody_data).map(x => ({ date: x.date, data: x.inbody_data }))} currentDate={r.date} />
                ) : (
                  <div className="p-3 bg-brand-bg/20 space-y-2 text-xs">
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                      {r.altura && <div><span className="text-brand-muted">Altura:</span> <b>{fmtNum(r.altura)} cm</b></div>}
                      {r.imc && <div><span className="text-brand-muted">IMC:</span> <b>{fmtNum(r.imc)}</b></div>}
                      {r.massa_gorda && <div><span className="text-brand-muted">M. gorda:</span> <b>{fmtNum(r.massa_gorda)} kg</b></div>}
                      {r.massa_ossea && <div><span className="text-brand-muted">M. óssea:</span> <b>{fmtNum(r.massa_ossea)} kg</b></div>}
                      {r.agua_corporal && <div><span className="text-brand-muted">Água:</span> <b>{fmtNum(r.agua_corporal)} kg</b></div>}
                      {r.proteina && <div><span className="text-brand-muted">Proteína:</span> <b>{fmtNum(r.proteina)} kg</b></div>}
                      {r.minerais && <div><span className="text-brand-muted">Minerais:</span> <b>{fmtNum(r.minerais)} kg</b></div>}
                      {r.ffm && <div><span className="text-brand-muted">FFM:</span> <b>{fmtNum(r.ffm)} kg</b></div>}
                      {r.inbody_score && <div><span className="text-brand-muted">InBody Score:</span> <b>{r.inbody_score}</b></div>}
                      {r.tmb && <div><span className="text-brand-muted">TMB:</span> <b>{r.tmb} kcal</b></div>}
                      {r.gordura_visceral && <div><span className="text-brand-muted">G. visceral:</span> <b>nível {r.gordura_visceral}</b></div>}
                      {r.cintura_cm && <div><span className="text-brand-muted">Cintura:</span> <b>{r.cintura_cm} cm</b></div>}
                      {r.cintura_anca && <div><span className="text-brand-muted">C/A:</span> <b>{r.cintura_anca}</b></div>}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        {records.length === 0 && <p className="text-center text-brand-muted py-6">Sem registos de peso.</p>}
      </div>
      {/* Add record dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}><DialogContent className="rounded-3xl border-brand-border" data-testid="add-weight-dialog"><DialogHeader><DialogTitle className="font-heading text-brand-text">Novo registo, Balança Prozis NEO</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs text-brand-muted mb-1 block">Data</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="rounded-xl border-brand-border" data-testid="weight-date" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-brand-muted mb-1 block">Peso (kg)</Label><Input type="number" step="0.1" value={form.peso} onChange={e => setForm({ ...form, peso: e.target.value })} className="rounded-xl border-brand-border" data-testid="weight-peso" required /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Altura (cm)</Label><Input type="number" step="0.1" value={form.altura} onChange={e => setForm({ ...form, altura: e.target.value })} className="rounded-xl border-brand-border" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Massa muscular (kg)</Label><Input type="number" step="0.1" value={form.massa_muscular} onChange={e => setForm({ ...form, massa_muscular: e.target.value })} className="rounded-xl border-brand-border" data-testid="weight-massa-muscular" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Massa gorda (kg)</Label><Input type="number" step="0.1" value={form.massa_gorda} onChange={e => setForm({ ...form, massa_gorda: e.target.value })} className="rounded-xl border-brand-border" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Gordura %</Label><Input type="number" step="0.1" value={form.gordura_pct} onChange={e => setForm({ ...form, gordura_pct: e.target.value })} className="rounded-xl border-brand-border" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Massa óssea (kg)</Label><Input type="number" step="0.1" value={form.massa_ossea} onChange={e => setForm({ ...form, massa_ossea: e.target.value })} className="rounded-xl border-brand-border" data-testid="weight-massa-ossea" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Água corporal (%)</Label><Input type="number" step="0.1" value={form.agua_corporal} onChange={e => setForm({ ...form, agua_corporal: e.target.value })} className="rounded-xl border-brand-border" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Gordura visceral (nível)</Label><Input type="number" step="1" value={form.gordura_visceral} onChange={e => setForm({ ...form, gordura_visceral: e.target.value })} className="rounded-xl border-brand-border" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">IMC <span className="text-brand-placeholder">(auto)</span></Label><Input type="number" step="0.1" value={form.imc} onChange={e => setForm({ ...form, imc: e.target.value })} placeholder="Calculado se vazio" className="rounded-xl border-brand-border" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">TMB (kcal)</Label><Input type="number" step="1" value={form.tmb} onChange={e => setForm({ ...form, tmb: e.target.value })} className="rounded-xl border-brand-border" data-testid="weight-tmb" /></div>
          </div>
          <button onClick={saveRecord} disabled={!form.peso} className="w-full bg-brand-green text-white rounded-full py-3 font-medium disabled:opacity-40" data-testid="save-weight-record">Guardar</button>
        </div>
      </DialogContent></Dialog>
      {/* Goals dialog */}
      <Dialog open={showGoals} onOpenChange={setShowGoals}><DialogContent className="rounded-3xl border-brand-border"><DialogHeader><DialogTitle className="font-heading text-brand-text">Objetivos</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs text-brand-muted mb-1 block">Peso objetivo (kg)</Label><Input type="number" step="0.1" value={goalForm.peso_objetivo} onChange={e => setGoalForm({ ...goalForm, peso_objetivo: e.target.value })} className="rounded-xl border-brand-border" /></div>
          <div><Label className="text-xs text-brand-muted mb-1 block">Foco do acompanhamento</Label><Input value={goalForm.foco} onChange={e => setGoalForm({ ...goalForm, foco: e.target.value })} placeholder="Ex: Manutenção saudável" className="rounded-xl border-brand-border" /></div>
          <button onClick={saveGoals} className="w-full bg-brand-green text-white rounded-full py-3 font-medium">Guardar</button>
        </div>
      </DialogContent></Dialog>
    </div>
  );
}

// ─── CUSTOM FIELDS SECTION ───
const CUSTOM_FIELD_TYPES = [
  { value: 'text', label: 'Texto curto' },
  { value: 'textarea', label: 'Texto longo' },
  { value: 'number', label: 'Número' },
  { value: 'date', label: 'Data' },
  { value: 'yesno', label: 'Sim/Não' },
];

function CustomFieldsSection({ patient, onRefresh }) {
  const [fields, setFields] = useState(patient?.custom_fields || []);
  const [showAdd, setShowAdd] = useState(false);
  const [newField, setNewField] = useState({ label: '', type: 'text', value: '' });

  const pid = patient?.user_id;

  const saveFields = async (updatedFields) => {
    try {
      await axios.put(`${API}/api/patients/${pid}`, { custom_fields: updatedFields });
      setFields(updatedFields);
      if (onRefresh) onRefresh();
    } catch { toast.error('Erro ao guardar'); }
  };

  const addField = () => {
    if (!newField.label.trim()) { toast.error('Nome do campo obrigatório'); return; }
    const updated = [...fields, { ...newField, id: Date.now().toString() }];
    saveFields(updated);
    setNewField({ label: '', type: 'text', value: '' });
    setShowAdd(false);
  };

  const updateValue = (idx, value) => {
    const updated = [...fields];
    updated[idx] = { ...updated[idx], value };
    saveFields(updated);
  };

  const removeField = (idx) => {
    const updated = fields.filter((_, i) => i !== idx);
    saveFields(updated);
  };

  const renderInput = (f, idx) => {
    switch (f.type) {
      case 'textarea': return <Textarea value={f.value || ''} onBlur={e => updateValue(idx, e.target.value)} onChange={e => { const u = [...fields]; u[idx] = { ...u[idx], value: e.target.value }; setFields(u); }} className="rounded-lg border-brand-border text-xs" rows={2} />;
      case 'number': return <Input type="number" value={f.value || ''} onBlur={e => updateValue(idx, e.target.value)} onChange={e => { const u = [...fields]; u[idx] = { ...u[idx], value: e.target.value }; setFields(u); }} className="w-24 rounded-lg border-brand-border text-xs h-7" />;
      case 'date': return <Input type="date" value={f.value || ''} onChange={e => updateValue(idx, e.target.value)} className="rounded-lg border-brand-border text-xs h-7" min="1920-01-01" max="2099-12-31" />;
      case 'yesno': return (
        <div className="flex gap-1.5">
          {['Sim', 'Não'].map(o => <button key={o} onClick={() => updateValue(idx, o)} className={`px-3 py-1 rounded-full text-[10px] font-medium ${f.value === o ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>{o}</button>)}
        </div>
      );
      default: return <Input value={f.value || ''} onBlur={e => updateValue(idx, e.target.value)} onChange={e => { const u = [...fields]; u[idx] = { ...u[idx], value: e.target.value }; setFields(u); }} className="rounded-lg border-brand-border text-xs h-7" />;
    }
  };

  return (
    <div className="mt-4 pt-3 border-t border-brand-border/50" data-testid="custom-fields-section">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] text-brand-muted uppercase tracking-wider">Campos personalizados</p>
        <button onClick={() => setShowAdd(true)} className="text-[10px] text-brand-green flex items-center gap-0.5" data-testid="add-custom-field-btn"><Plus size={10} /> Adicionar</button>
      </div>
      {fields.length === 0 && !showAdd && <p className="text-[10px] text-brand-placeholder">Sem campos personalizados.</p>}
      {fields.length > 0 && (
        <div className="space-y-2">
          {fields.map((f, idx) => (
            <div key={f.id || idx} className="flex items-start gap-2 group" data-testid={`custom-field-${idx}`}>
              <div className="flex-1">
                <p className="text-[10px] text-brand-muted mb-0.5">{f.label}</p>
                {renderInput(f, idx)}
              </div>
              <button onClick={() => removeField(idx)} className="hidden group-hover:block text-brand-placeholder hover:text-red-500 mt-3"><Trash size={11} /></button>
            </div>
          ))}
        </div>
      )}
      {showAdd && (
        <div className="mt-2 p-3 bg-brand-bg/30 rounded-xl space-y-2">
          <Input value={newField.label} onChange={e => setNewField({ ...newField, label: e.target.value })} placeholder="Nome do campo" className="rounded-lg border-brand-border text-xs h-7" data-testid="custom-field-label" onKeyDown={e => { if (e.key === 'Enter') addField(); }} />
          <div className="flex gap-1.5">
            {CUSTOM_FIELD_TYPES.map(t => <button key={t.value} onClick={() => setNewField({ ...newField, type: t.value })} className={`px-2 py-1 rounded-full text-[9px] ${newField.type === t.value ? 'bg-brand-green text-white' : 'bg-white text-brand-muted border border-brand-border'}`}>{t.label}</button>)}
          </div>
          <div className="flex gap-1.5">
            <button onClick={addField} className="bg-brand-green text-white rounded-full px-3 py-1.5 text-[10px] font-medium" data-testid="confirm-custom-field">Adicionar</button>
            <button onClick={() => setShowAdd(false)} className="text-[10px] text-brand-muted">Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── SYMPTOMS SECTION ───
function SymptomsSection({ patientId }) {
  const confirmDelete = useConfirmDelete();
  const [symptoms, setSymptoms] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ symptom: '', intensity: 5, notes: '' });
  const fetch = useCallback(async () => { try { const { data } = await axios.get(`${API}/api/patients/${patientId}/symptoms`); setSymptoms(data || []); } catch {} }, [patientId]);
  useEffect(() => { fetch(); }, [fetch]);
  const save = async () => {
    try { await axios.post(`${API}/api/patients/${patientId}/symptoms`, form); toast.success('Sintoma registado'); setShowAdd(false); setForm({ symptom: '', intensity: 5, notes: '' }); fetch(); } catch { toast.error('Erro'); }
  };
  const resolve = async (id) => { await axios.put(`${API}/api/patients/${patientId}/symptoms/${id}`, { resolved: true }); fetch(); };
  const del = async (id) => {
    const s = symptoms.find(x => x.id === id);
    const ok = await confirmDelete({ description: `Vai apagar o sintoma "${s?.symptom || ''}" registado em ${s?.date || ''}.` });
    if (!ok) return;
    await axios.delete(`${API}/api/patients/${patientId}/symptoms/${id}`); fetch();
  };
  const COMMON = ['Azia','Obstipação','Náuseas','Cansaço','Insónia','Dores de cabeça','Inchaço','Fome excessiva','Falta de apetite'];
  return (
    <div className="mt-3" data-testid="symptoms-section">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] text-brand-muted uppercase tracking-wider">Sintomas e queixas</p>
        <button onClick={() => setShowAdd(true)} className="text-[10px] text-brand-green flex items-center gap-0.5"><Plus size={10} /> Registar</button>
      </div>
      {symptoms.filter(s => !s.resolved).length === 0 && !showAdd ? <p className="text-xs text-brand-placeholder">Sem sintomas activos.</p> : (
        <div className="space-y-1.5">
          {symptoms.filter(s => !s.resolved).map(s => (
            <div key={s.id} className="flex items-center gap-2 bg-white border border-brand-border rounded-lg px-3 py-2 group">
              <div className="flex-1">
                <span className="text-xs font-medium text-brand-text">{s.symptom}</span>
                <span className="ml-2 text-[9px] text-brand-muted">intensidade {s.intensity}/10</span>
                {s.notes && <Md as="p" className="text-[9px] text-brand-muted">{s.notes}</Md>}
              </div>
              <span className="text-[8px] text-brand-placeholder">{fmtDatePt(s.date)}</span>
              <button onClick={() => resolve(s.id)} className="hidden group-hover:block text-[9px] text-brand-green hover:underline">Resolvido</button>
              <button onClick={() => del(s.id)} className="hidden group-hover:block text-brand-placeholder hover:text-red-500"><Trash size={11} /></button>
            </div>
          ))}
          {symptoms.filter(s => s.resolved).length > 0 && (
            <details className="text-[9px] text-brand-muted mt-1"><summary className="cursor-pointer">Resolvidos ({symptoms.filter(s => s.resolved).length})</summary>
              {symptoms.filter(s => s.resolved).map(s => <p key={s.id} className="mt-0.5 line-through">{s.symptom} (resolvido {s.resolved_at})</p>)}
            </details>
          )}
        </div>
      )}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Registar sintoma</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex flex-wrap gap-1">{COMMON.map(c => <button key={c} onClick={() => setForm({ ...form, symptom: c })} className={`px-2 py-1 rounded-full text-[10px] ${form.symptom === c ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>{c}</button>)}</div>
            <Input value={form.symptom} onChange={e => setForm({ ...form, symptom: e.target.value })} placeholder="Outro sintoma" className="rounded-xl border-brand-border" />
            <div><Label className="text-xs text-brand-muted mb-1 block">Intensidade: {form.intensity}/10</Label><input type="range" min="1" max="10" value={form.intensity} onChange={e => setForm({ ...form, intensity: parseInt(e.target.value) })} className="w-full" /></div>
            <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Notas (opcional)" className="rounded-xl border-brand-border" />
            <button onClick={save} className="w-full bg-brand-green text-white rounded-full py-3 font-medium">Registar</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── PATIENT CHALLENGES HISTORY ───
function PatientChallengesHistory({ patientId }) {
  const [history, setHistory] = useState([]);
  useEffect(() => {
    (async () => { try { const { data } = await axios.get(`${API}/api/patients/${patientId}/challenges-history`); setHistory(data || []); } catch {} })();
  }, [patientId]);
  if (history.length === 0) return null;
  return (
    <div className="mt-3 bg-white border border-brand-border rounded-xl p-3" data-testid="patient-challenges-history">
      <p className="text-[10px] text-brand-muted uppercase tracking-wider mb-2">Desafios</p>
      <div className="space-y-1.5">
        {history.map(h => (
          <div key={h.challenge_id} className="flex items-center justify-between bg-brand-bg/30 rounded-lg px-3 py-2">
            <div>
              <p className="text-xs font-medium text-brand-text">{h.title}</p>
              <p className="text-[9px] text-brand-muted">{fmtDatePt(h.start_date)} a {fmtDatePt(h.end_date)}</p>
            </div>
            <div className="text-right">
              <span className={`text-xs font-medium ${h.adherence >= 70 ? 'text-brand-green' : 'text-brand-muted'}`}>{h.adherence}%</span>
              <p className="text-[8px] text-brand-muted">{h.days_completed} dias</p>
              <span className={`text-[8px] px-1.5 py-0.5 rounded-full ${h.active ? 'bg-brand-mint/20 text-brand-green' : 'bg-brand-bg text-brand-muted'}`}>{h.active ? 'Ativo' : 'Concluído'}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── PRE-CONSULTATION RESPONSES (simple view) ───

// ─── DRIVE TAB (patient files) ───
// ─── SCHEDULED MESSAGES TAB ───
function MensagensProgramadasTab({ patientId }) {
  const confirmDelete = useConfirmDelete();
  const [messages, setMessages] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ content: '', send_date: '', trigger_type: 'date' });
  const fetch = useCallback(async () => { try { const { data } = await axios.get(`${API}/api/patients/${patientId}/scheduled-messages`); setMessages(data); } catch {} }, [patientId]);
  useEffect(() => { fetch(); }, [fetch]);
  const save = async () => {
    if (!form.content.trim() || !form.send_date) { toast.error('Preenche a mensagem e a data'); return; }
    try {
      if (editId) { await axios.put(`${API}/api/scheduled-messages/${editId}`, form); }
      else { await axios.post(`${API}/api/patients/${patientId}/scheduled-messages`, form); }
      toast.success(editId ? 'Mensagem actualizada' : 'Mensagem programada');
      setShowForm(false); setEditId(null); setForm({ content: '', send_date: '', trigger_type: 'date' }); fetch();
    } catch { toast.error('Erro'); }
  };
  const cancel = async (id) => {
    const m = messages.find(x => x.id === id);
    const ok = await confirmDelete({ description: `Vai cancelar e apagar a mensagem programada para ${m?.send_date || ''}.`, confirmLabel: 'Eliminar mensagem' });
    if (!ok) return;
    await axios.delete(`${API}/api/scheduled-messages/${id}`); toast.success('Mensagem cancelada'); fetch();
  };
  const STATUS_STYLES = { agendada: 'bg-amber-50 text-amber-700', enviada: 'bg-green-50 text-green-700', cancelada: 'bg-gray-100 text-gray-400' };
  const STATUS_LABELS = { agendada: 'Agendada', enviada: 'Enviada', cancelada: 'Cancelada' };
  return (
    <div data-testid="tab-scheduled-messages">
      <div className="flex justify-end mb-3">
        <button onClick={() => { setEditId(null); setForm({ content: '', send_date: new Date(Date.now() + 7*86400000).toISOString().split('T')[0], trigger_type: 'date' }); setShowForm(true); }} className="bg-brand-green text-white rounded-full px-4 py-2 text-sm font-medium flex items-center gap-1" data-testid="add-scheduled-msg"><Plus size={14} /> Nova mensagem</button>
      </div>
      {messages.length === 0 ? <p className="text-center text-brand-muted py-8">Sem mensagens programadas.</p> : (
        <div className="space-y-2">
          {messages.map(m => (
            <div key={m.id} className={`bg-white border border-brand-border rounded-2xl p-3 ${m.status === 'cancelada' ? 'opacity-50' : ''}`} data-testid={`scheduled-msg-${m.id}`}>
              <div className="flex items-start justify-between mb-1">
                <span className={`text-[9px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[m.status] || ''}`}>{STATUS_LABELS[m.status] || m.status}</span>
                <span className="text-[10px] text-brand-muted">{fmtDatePt(m.send_date)}</span>
              </div>
              <p className="text-xs text-brand-text">{m.content}</p>
              {m.status === 'agendada' && (
                <div className="flex gap-2 mt-2">
                  <button onClick={() => { setEditId(m.id); setForm({ content: m.content, send_date: m.send_date, trigger_type: m.trigger_type || 'date' }); setShowForm(true); }} className="text-[10px] text-brand-green hover:underline">Editar</button>
                  <button onClick={() => cancel(m.id)} className="text-[10px] text-red-500 hover:underline">Cancelar</button>
                </div>
              )}
              {m.status === 'enviada' && m.sent_at && <p className="text-[9px] text-brand-placeholder mt-1">Enviada a {fmtDatePt(m.sent_at?.slice(0, 10))}</p>}
            </div>
          ))}
        </div>
      )}
      <Dialog open={showForm} onOpenChange={v => { if (!v) { setShowForm(false); setEditId(null); } }}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">{editId ? 'Editar mensagem' : 'Nova mensagem programada'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-brand-muted mb-1 block">Data de envio</Label><Input type="date" value={form.send_date} onChange={e => setForm({ ...form, send_date: e.target.value })} className="rounded-xl border-brand-border" min={new Date().toISOString().split('T')[0]} /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Mensagem</Label><Textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} placeholder="Escreve a mensagem que a cliente vai receber..." className="rounded-xl border-brand-border" rows={4} /></div>
            <button onClick={save} className="w-full bg-brand-green text-white rounded-full py-3 font-medium">{editId ? 'Guardar alterações' : 'Programar mensagem'}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function DriveTab({ patientId }) {
  const confirmDelete = useConfirmDelete();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [libraryItems, setLibraryItems] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [category, setCategory] = useState('todos');
  const [uploadCategory, setUploadCategory] = useState('outros');
  const [editingFile, setEditingFile] = useState(null);
  const CATEGORIES = [
    { key: 'todos', label: 'Todos' },
    { key: 'plano_alimentar', label: 'Plano Alimentar' },
    { key: 'analises', label: 'Análises' },
    { key: 'receita_medica', label: 'Receita Médica' },
    { key: 'ecografias', label: 'Ecografias' },
    { key: 'planos', label: 'Planos' },
    { key: 'suplementacao', label: 'Suplementação' },
    { key: 'fotografias', label: 'Fotografias' },
    { key: 'outros', label: 'Outros' },
  ];
  const fetch = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/patients/${patientId}/drive`); setFiles(data); } catch {}
  }, [patientId]);
  useEffect(() => { fetch(); }, [fetch]);
  const totalSize = files.reduce((s, f) => s + (f.size || 0), 0);
  const limitMB = 500;
  const usedMB = (totalSize / (1024 * 1024)).toFixed(1);
  const handleUpload = async (file) => {
    if (!file) return; setUploading(true);
    try { const fd = new FormData(); fd.append('file', file); await axios.post(`${API}/api/patients/${patientId}/drive/upload?category=${uploadCategory}`, fd); toast.success('Ficheiro carregado'); fetch(); } catch(e) { toast.error(e.response?.data?.detail || 'Erro'); }
    finally { setUploading(false); }
  };
  const handleDelete = async (id) => {
    const f = files.find(x => x.id === id);
    const ok = await confirmDelete({ description: `Vai apagar o ficheiro "${f?.filename || ''}" do Drive da paciente.` });
    if (!ok) return;
    await axios.delete(`${API}/api/patients/${patientId}/drive/${id}`); toast.success('Eliminado'); fetch();
  };
  const handleUpdate = async (id, data) => { await axios.put(`${API}/api/patients/${patientId}/drive/${id}`, data); fetch(); };
  const toggleVisibility = async (f) => { await handleUpdate(f.id, { visible_to_client: !f.visible_to_client }); toast.success(f.visible_to_client ? 'Ficheiro ocultado' : 'Ficheiro visível para a cliente'); };
  const handleDownload = async (f) => {
    try {
      const resp = await axios.get(`${API}/api/files/${f.file_path}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a'); a.href = url; a.download = f.filename; a.click();
    } catch { toast.error('Erro ao descarregar'); }
  };
  const openLibrary = async () => {
    try { const { data } = await axios.get(`${API}/api/contents`); setLibraryItems(data || []); setSelectedIds([]); setShowLibrary(true); } catch {}
  };
  const linkSelected = async () => {
    if (selectedIds.length === 0) return;
    try { const { data } = await axios.post(`${API}/api/patients/${patientId}/drive/link-from-library`, { content_ids: selectedIds }); toast.success(`${data.linked} ficheiro(s) associado(s)`); setShowLibrary(false); fetch(); } catch { toast.error('Erro'); }
  };
  const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const EXT_COLORS = { pdf: 'bg-red-50 text-red-600', xlsx: 'bg-emerald-50 text-emerald-600', xls: 'bg-emerald-50 text-emerald-600', csv: 'bg-blue-50 text-blue-600', jpg: 'bg-amber-50 text-amber-600', jpeg: 'bg-amber-50 text-amber-600', png: 'bg-amber-50 text-amber-600', mp4: 'bg-purple-50 text-purple-600' };
  const filtered = category === 'todos' ? files : files.filter(f => (f.category || 'outros') === category);
  return (
    <div data-testid="tab-drive">
      {/* Storage usage bar */}
      <div className="mb-3 flex items-center gap-2">
        <div className="flex-1 bg-brand-bg rounded-full h-2 overflow-hidden">
          <div className="bg-brand-green h-full rounded-full transition-all" style={{ width: `${Math.min((totalSize / (limitMB * 1024 * 1024)) * 100, 100)}%` }} />
        </div>
        <span className="text-[10px] text-brand-muted whitespace-nowrap">{usedMB} / {limitMB} MB</span>
      </div>
      {/* Category filter */}
      <div className="flex gap-1.5 mb-3 flex-wrap">
        {CATEGORIES.map(c => (
          <button key={c.key} onClick={() => setCategory(c.key)} className={`px-2.5 py-1 rounded-full text-[10px] font-medium ${category === c.key ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`} data-testid={`drive-cat-${c.key}`}>{c.label}</button>
        ))}
      </div>
      <div className="flex justify-end mb-4 gap-2">
        <button onClick={openLibrary} className="border border-brand-border text-brand-green rounded-full px-4 py-2 text-sm font-medium hover:bg-brand-green/5" data-testid="choose-from-library-btn">
          Escolher da biblioteca
        </button>
        <div className="flex items-center gap-1">
          <select value={uploadCategory} onChange={e => setUploadCategory(e.target.value)} className="text-[10px] border border-brand-border rounded-lg px-2 py-1.5 bg-white" data-testid="upload-category-select">
            {CATEGORIES.filter(c => c.key !== 'todos').map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <label className={`bg-brand-green text-white rounded-full px-4 py-2 text-sm font-medium flex items-center gap-1 cursor-pointer ${uploading ? 'opacity-50' : ''}`}>
            <UploadSimple size={14} /> {uploading ? 'A carregar...' : 'Carregar'}
            <input type="file" onChange={e => handleUpload(e.target.files?.[0])} className="hidden" disabled={uploading} data-testid="drive-upload" />
          </label>
        </div>
      </div>
      {filtered.length === 0 ? <p className="text-center text-brand-muted py-8">Sem ficheiros{category !== 'todos' ? ` em "${CATEGORIES.find(c=>c.key===category)?.label}"` : ''}.</p> : (
        <div className="space-y-2">
          {filtered.map(f => {
            const ext = f.filename?.split('.').pop()?.toLowerCase() || '';
            const isLibrary = f.source === 'library';
            const catLabel = CATEGORIES.find(c => c.key === f.category)?.label;
            return (
              <div key={f.id} className={`bg-white border rounded-2xl p-3 flex items-center gap-3 ${isLibrary ? 'border-brand-green/20' : 'border-brand-border'}`} data-testid={`drive-file-${f.id}`}>
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-[10px] font-bold uppercase ${EXT_COLORS[ext] || 'bg-brand-bg text-brand-muted'}`}>{ext || 'DOC'}</div>
                <div className="flex-1 min-w-0">
                  <button onClick={() => handleDownload(f)} className="text-sm font-medium text-brand-text hover:text-brand-green truncate block">{f.title || f.filename}</button>
                  <p className="text-[10px] text-brand-muted">
                    {fmtDatePt(f.created_at?.slice(0, 10))}
                    {f.size ? ` · ${(f.size / 1024).toFixed(0)} KB` : ''}
                    {catLabel && <span className="ml-1 text-brand-green/70">{catLabel}</span>}
                    {isLibrary && <span className="ml-1 text-brand-green">(biblioteca)</span>}
                    {f.visible_to_client === false && <span className="ml-1 text-amber-500">(só nutri)</span>}
                  </p>
                  {f.description && <p className="text-[9px] text-brand-placeholder truncate">{f.description}</p>}
                </div>
                <button onClick={() => toggleVisibility(f)} className={`p-1.5 ${f.visible_to_client === false ? 'text-amber-500' : 'text-brand-placeholder hover:text-amber-500'}`} title={f.visible_to_client === false ? 'Tornar visível' : 'Ocultar da cliente'}>
                  {f.visible_to_client === false ? <EyeSlash size={13} /> : <Eye size={13} />}
                </button>
                <button onClick={() => handleDelete(f.id)} className="p-1.5 text-brand-placeholder hover:text-red-500"><Trash size={14} /></button>
              </div>
            );
          })}
        </div>
      )}
      {/* Library picker dialog */}
      <Dialog open={showLibrary} onOpenChange={setShowLibrary}>
        <DialogContent className="rounded-3xl border-brand-border max-h-[80vh] overflow-y-auto max-w-lg">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Escolher da biblioteca</DialogTitle></DialogHeader>
          <div className="space-y-1.5 mb-3">
            {libraryItems.length === 0 ? <p className="text-xs text-brand-muted py-4 text-center">Biblioteca vazia.</p> : libraryItems.map(item => {
              const sel = selectedIds.includes(item.id);
              return (
                <button key={item.id} onClick={() => toggleSelect(item.id)} className={`w-full text-left px-3 py-2.5 rounded-xl border ${sel ? 'border-brand-green bg-brand-green/5' : 'border-brand-border hover:border-brand-green/30'}`} data-testid={`lib-item-${item.id}`}>
                  <p className="text-sm text-brand-text">{item.title || item.file_name || 'Sem título'}</p>
                  <p className="text-[9px] text-brand-muted">{item.type || item.category || ''}</p>
                </button>
              );
            })}
          </div>
          <button onClick={linkSelected} disabled={selectedIds.length === 0} className="w-full bg-brand-green text-white rounded-full py-3 font-medium disabled:opacity-40" data-testid="confirm-link-library">
            Associar {selectedIds.length > 0 ? `(${selectedIds.length})` : ''}
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── EVOLUÇÃO UNIFIED TAB (weight + physical assessment merged) ───
function EvolucaoUnifiedTab({ patientId, patient }) {
  return (
    <div data-testid="tab-evolucao-unified" className="space-y-6">
      {isGravidaPatient(patient) && (
        <div className="bg-white border border-brand-border rounded-2xl p-4" data-testid="evolucao-atalah">
          <AtalahChart patientId={patientId} editable />
        </div>
      )}
      <EvolucaoTab patientId={patientId} />
      <div className="border-t border-brand-border pt-4">
        <AvaliacaoTab patientId={patientId} />
      </div>
    </div>
  );
}

// ─── PAGAMENTOS POR PACIENTE TAB (Item 10.a — Fase 3) ───
// PROMPT 5 — Item 1: tabs extraídos para /components/admin/patient-tabs/ — ver imports no topo do ficheiro.

// ─── NECESSIDADES NUTRICIONAIS TAB ───

// ═══ MAIN PATIENT DETAIL PAGE ═══
const NEW_TABS = [
  { v: 'resumo', l: 'Resumo' },
  { v: 'consultas', l: 'Consultas' },
  { v: 'clinico', l: 'Clínico' },
  { v: 'evolucao', l: 'Evolução' },
  { v: 'plano', l: 'Plano' },
  { v: 'acompanhamento', l: 'Acompanhamento' },
  { v: 'documentos', l: 'Documentos' },
  { v: 'dados', l: 'Dados e administrativo' },
];
const NEW_TAB_KEYS = NEW_TABS.map(t => t.v);
const NEW_TAB_LABELS = NEW_TABS.reduce((acc, t) => ({ ...acc, [t.v]: t.l }), {});

export default function PacienteDetailPage() {
  useRequireAdmin();
  const confirmDelete = useConfirmDelete();
  const { userId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabOrder, setTabOrder] = useState([]);
  const [showTabOrder, setShowTabOrder] = useState(false);
  const [tempOrder, setTempOrder] = useState([]);
  const [patientTags, setPatientTags] = useState([]);
  const [program, setProgram] = useState({});
  const [showProgramForm, setShowProgramForm] = useState(false);
  const [progForm, setProgForm] = useState({ total_sessions: '', completed_sessions: '', start_date: '', end_date: '', duration: '', program_value: '' });
  const [nextAppointment, setNextAppointment] = useState(null);
  const tabsWrapperRef = useRef(null);
  const tabsListRef = useRef(null);
  const activeTab = NEW_TAB_KEYS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'resumo';
  const setActiveTab = (v) => setSearchParams(prev => { const p = new URLSearchParams(prev); p.set('tab', v); return p; }, { replace: true });

  const fetchPatient = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/patients/${userId}`, { withCredentials: true });
      setPatient(data);
    } catch {
      toast.error('Paciente não encontrado');
      navigate('/admin/pacientes');
    } finally {
      setLoading(false);
    }
  }, [userId, navigate]);

  useEffect(() => { fetchPatient(); }, [fetchPatient]);
  useEffect(() => { (async () => { try { const { data } = await axios.get(`${API}/api/patient-tags`); setPatientTags(data?.tags || []); } catch {} })(); }, []);
  useEffect(() => {
    (async () => {
      try { const { data } = await axios.get(`${API}/api/patients/${userId}/program-status`); setProgram(data); setProgForm({ total_sessions: data.total_sessions || '', completed_sessions: data.completed_sessions || '', start_date: data.start_date || '', end_date: data.end_date || '', duration: data.duration || '', program_value: data.program_value || '' }); } catch {}
      try {
        const { data } = await axios.get(`${API}/api/patients/${userId}/appointments`);
        const today = new Date().toISOString().split('T')[0];
        const upcoming = (data || []).filter(a => a.date >= today).sort((a, b) => a.date.localeCompare(b.date));
        setNextAppointment(upcoming[0] || null);
      } catch {}
    })();
  }, [userId]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/auth/me`);
        const incoming = data.tab_order || [];
        // Regra 4 out 2026: não migrar tab_order antigo (20 tabs) — só aceita se já estiver no
        // novo formato de 8 separadores; caso contrário repõe a ordem por defeito.
        const isNewFormat = incoming.length > 0 && incoming.every(t => NEW_TAB_KEYS.includes(t));
        setTabOrder(isNewFormat ? incoming : []);
      } catch {}
    })();
  }, []);
  const saveProgram = async () => {
    const payload = {
      ...progForm,
      program_value: progForm.program_value === '' || progForm.program_value === null ? null : Number(progForm.program_value),
      total_sessions: progForm.total_sessions === '' || progForm.total_sessions === null ? null : parseInt(progForm.total_sessions, 10),
      completed_sessions: progForm.completed_sessions === '' || progForm.completed_sessions === null ? null : parseInt(progForm.completed_sessions, 10),
    };
    try { await axios.put(`${API}/api/patients/${userId}/program-status`, payload); setProgram({ ...program, ...payload }); toast.success('Programa atualizado'); setShowProgramForm(false); } catch { toast.error('Erro'); }
  };
  const openNewProgram = () => { setProgForm({ total_sessions: '', completed_sessions: '0', start_date: new Date().toISOString().split('T')[0], end_date: '', duration: '', program_value: '' }); setShowProgramForm(true); };
  // Bugfix: quando o admin tem uma ordem de tabs personalizada guardada ("Organizar tabs"), ela só
  // chega depois do 1.º render (pedido assíncrono acima) — reordenar os filhos do TabsList nesse
  // momento faz o browser "compensar" a mudança de layout (scroll anchoring) e desloca o scroll
  // horizontal quase ao máximo, escondendo os primeiros separadores atrás da sidebar. Forçar
  // scrollLeft=0 depois da ordem chegar corrige isto de forma determinística.
  useEffect(() => {
    const raf = requestAnimationFrame(() => { if (tabsListRef.current) tabsListRef.current.scrollLeft = 0; });
    return () => cancelAnimationFrame(raf);
  }, [tabOrder]);
  const saveTabOrder = async (order) => { setTabOrder(order); try { await axios.put(`${API}/api/my-profile`, { tab_order: order }); } catch {} };
  const moveTab = (idx, dir) => { const arr = [...tempOrder]; const ni = idx + dir; if (ni < 0 || ni >= arr.length) return; [arr[idx], arr[ni]] = [arr[ni], arr[idx]]; setTempOrder(arr); };

  if (loading) return <div className="text-center py-12 text-brand-muted">A carregar...</div>;
  if (!patient) return null;

  return (
    <div className="fade-in" data-testid="paciente-detail-page">
      <Link to="/admin/pacientes" className="text-sm text-brand-green mb-3 hover:underline flex items-center gap-1 no-underline" data-testid="back-to-patients"><CaretLeft size={14} /> Pacientes</Link>
      
      {/* ═══ PATIENT HEADER BLOCK (fixo, igual em todos os separadores) ═══ */}
      <div className="bg-white border border-brand-border rounded-2xl p-4 mb-4">
        <div className="flex items-start gap-3 mb-3">
          {patient.avatar_path ? (
            <img src={`${API}/api/files/${patient.avatar_path}`} alt="" className="w-12 h-12 rounded-full object-cover border-2 border-brand-mint/30 flex-shrink-0" />
          ) : (
            <div className="w-12 h-12 rounded-full bg-brand-mint/30 flex items-center justify-center flex-shrink-0">
              <span className="font-heading font-semibold text-brand-green text-lg">{(patient.name || '?')[0].toUpperCase()}</span>
            </div>
          )}
          <div className="flex-1">
            <h2 className="font-heading font-medium text-xl text-brand-text" data-testid="patient-name">{patient.name}</h2>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <span className="text-xs text-brand-muted">{patient.email}</span>
              {(patient.patient_types || [patient.patient_type]).filter(Boolean).map(t => {
                const tag = patientTags.find(pt => pt.value === t);
                // Item 2 — Fase 4: tags legadas (sem entry em patientTags) ainda têm de mostrar o botão de remoção.
                const label = tag?.label || t;
                return (
                  <span key={t} className={`text-[10px] px-2 py-0.5 rounded-full inline-flex items-center gap-1 group ${tag ? 'bg-brand-bg text-brand-muted' : 'bg-amber-50 text-amber-700'}`}>
                    {label}
                    <button
                      type="button"
                      onClick={async () => {
                        const ok = await confirmDelete({ description: `Vai apagar a tag "${label}" deste perfil.` });
                        if (!ok) return;
                        const remainingTypes = (patient.patient_types || [patient.patient_type]).filter(Boolean).filter(x => x !== t);
                        try {
                          await axios.put(`${API}/api/patients/${userId}`, { patient_types: remainingTypes, patient_type: remainingTypes[0] || '' });
                          toast.success('Tag removida'); fetchPatient();
                        } catch { toast.error('Erro ao apagar tag'); }
                      }}
                      className="opacity-40 group-hover:opacity-100 transition-opacity text-brand-muted hover:text-red-500 text-[10px] leading-none"
                      title="Apagar tag"
                      data-testid={`remove-patient-type-${t}`}
                    >×</button>
                  </span>
                );
              })}
              {(patient.tags || []).map(t => (
                <span key={`tag-${t}`} className="text-[10px] bg-brand-mint/15 text-brand-green px-2 py-0.5 rounded-full inline-flex items-center gap-1 group">
                  {t}
                  <button
                    type="button"
                    onClick={async () => {
                      const ok = await confirmDelete({ description: `Vai apagar a tag "${t}" deste perfil.` });
                      if (!ok) return;
                      const remaining = (patient.tags || []).filter(x => x !== t);
                      try {
                        await axios.put(`${API}/api/patients/${userId}`, { tags: remaining });
                        toast.success('Tag removida'); fetchPatient();
                      } catch { toast.error('Erro ao apagar tag'); }
                    }}
                    className="opacity-40 group-hover:opacity-100 transition-opacity hover:text-red-500 text-[10px] leading-none"
                    title="Apagar tag"
                    data-testid={`remove-free-tag-${t}`}
                  >×</button>
                </span>
              ))}
            </div>
          </div>
        </div>
        {/* Alertas */}
        {patient.pre_consultation_badge && (
          <div className="bg-brand-gold/10 border border-brand-gold/30 rounded-xl p-3 mb-2 flex items-center justify-between" data-testid="pre-consult-badge">
            <div>
              <p className="text-xs font-medium text-brand-gold-hover">Novo questionário pré-consulta</p>
              <p className="text-[10px] text-brand-muted">A cliente preencheu o questionário. Revê as respostas na Anamnese.</p>
            </div>
            <button onClick={async () => { await axios.put(`${API}/api/patients/${userId}/pre-consultation-reviewed`, { reviewed: true }); fetchPatient(); window.dispatchEvent(new Event('nav-badges-refresh')); toast.success('Marcado como revisto'); }} className="text-[10px] text-brand-green hover:underline flex-shrink-0" data-testid="mark-reviewed-btn">Marcar como revisto</button>
          </div>
        )}
        {!patient.therapeutic_purpose && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-2.5 mb-2 flex items-center gap-2" data-testid="tp-missing-badge">
            <Warning size={13} className="text-amber-600 flex-shrink-0" />
            <p className="text-xs text-amber-700">Falta registar a finalidade terapêutica</p>
            <button onClick={() => setActiveTab('dados')} className="text-[10px] text-brand-green hover:underline ml-auto flex-shrink-0" data-testid="tp-missing-goto-btn">Preencher</button>
          </div>
        )}
        {/* Quick info cards */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {patient.birth_date && (() => { const bd=new Date(patient.birth_date); const today=new Date(); const dd=Math.floor((today-bd)/86400000); const tm=(today.getFullYear()-bd.getFullYear())*12+today.getMonth()-bd.getMonth(); let at; if(tm<1){const w=Math.floor(dd/7);at=w+" semana"+(w!==1?"s":"");} else if(tm<24){const w=Math.floor((dd-tm*30.44)/7);at=tm+" mes"+(tm!==1?"es":"")+(w>0?" e "+w+" sem.":"");} else{let a=today.getFullYear()-bd.getFullYear();if(today.getMonth()<bd.getMonth()||(today.getMonth()===bd.getMonth()&&today.getDate()<bd.getDate()))a--;at=a+" anos";} return <div className="bg-brand-bg rounded-xl p-2 text-center"><p className="text-sm font-heading font-semibold text-brand-text">{at}</p><p className="text-[9px] text-brand-muted">idade</p></div>; })()}
          {patient.dum && (() => { const dum = new Date(patient.dum); const diff = Math.floor((new Date() - dum) / 86400000); const w = Math.floor(diff / 7); const d = diff % 7; if (diff < 0 || w > 42) return null; return <div className="bg-pink-50 rounded-xl p-2 text-center"><p className="text-sm font-heading font-semibold text-pink-600">{w}s+{d}d</p><p className="text-[9px] text-brand-muted">IG</p></div>; })()}
          {patient.dum && (() => { const dpp = new Date(patient.dum); dpp.setDate(dpp.getDate() + 280); return <div className="bg-pink-50 rounded-xl p-2 text-center"><p className="text-[10px] font-heading font-semibold text-pink-600">{dpp.getDate()}/{dpp.getMonth()+1}</p><p className="text-[9px] text-brand-muted">DPP</p></div>; })()}
          {patient.baby_birth_date && (isPosPartoPatient(patient) || isBabyPatient(patient)) && (() => { const bd = new Date(patient.baby_birth_date); const dd = Math.floor((new Date() - bd) / 86400000); if (dd < 0) return null; const w = Math.floor(dd / 7); const m = Math.floor(dd / 30.44); return <div className="bg-purple-50 rounded-xl p-2 text-center" data-testid="baby-age-card"><p className="text-sm font-heading font-semibold text-purple-600">{w}s ({m}m)</p><p className="text-[9px] text-brand-muted">idade bebé</p></div>; })()}
          {patient.delivery_date && isPosPartoPatient(patient) && (() => { const d = new Date(patient.delivery_date); const dd = Math.floor((new Date() - d) / 86400000); if (dd < 0) return null; const w = Math.floor(dd / 7); return <div className="bg-purple-50 rounded-xl p-2 text-center" data-testid="postparto-header"><p className="text-sm font-heading font-semibold text-purple-600">{w}s</p><p className="text-[9px] text-brand-muted">pós-parto</p></div>; })()}
          {patient._stats?.total_consultations > 0 && <div className="bg-brand-bg rounded-xl p-2 text-center"><p className="text-sm font-heading font-semibold text-brand-text">{patient._stats.total_consultations}</p><p className="text-[9px] text-brand-muted">consultas</p></div>}
          {patient._stats?.last_interaction && <div className="bg-brand-bg rounded-xl p-2 text-center"><p className="text-[10px] font-heading font-semibold text-brand-text">{patient._stats.last_interaction.slice(5)}</p><p className="text-[9px] text-brand-muted">última int.</p></div>}
        </div>
        {/* Programa e fase atual + próxima consulta */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
          <div className="bg-brand-bg/60 rounded-xl p-3 flex items-center justify-between" data-testid="header-program-widget">
            <div>
              <p className="text-[10px] text-brand-muted uppercase tracking-wider">Programa</p>
              <ProgramSummary program={program} />
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button onClick={() => setShowProgramForm(true)} className="text-[10px] text-brand-green flex items-center gap-1" data-testid="edit-program-btn"><PencilSimple size={11} /> Editar</button>
              <button onClick={openNewProgram} className="text-[10px] text-brand-green flex items-center gap-1 bg-brand-green/5 px-2 py-0.5 rounded-full" data-testid="new-program-btn"><Plus size={10} /> Novo</button>
            </div>
          </div>
          {program?.id && <div className="col-span-full"><ProgramInstallments program={program} patientId={userId} /></div>}
          <div className="col-span-full" data-testid="header-followup"><FollowupControls patient={patient} onChanged={fetchPatient} /></div>
          <div className="bg-brand-bg/60 rounded-xl p-3" data-testid="header-next-appointment">
            <p className="text-[10px] text-brand-muted uppercase tracking-wider">Próxima consulta</p>
            {nextAppointment ? (
              <p className="text-sm font-heading font-semibold text-brand-text">{fmtDateTime(nextAppointment.date, nextAppointment.time)}</p>
            ) : (
              <p className="text-xs text-brand-placeholder">Sem marcação futura</p>
            )}
          </div>
        </div>
        {/* Coverage indicator */}
        {patient.in_coverage && (
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-3 mt-2 flex items-center justify-between">
            <span className="text-xs text-blue-700">Em cobertura (titular: {patient.original_nutritionist || '?'})</span>
            <button onClick={async () => { await axios.put(`${API}/api/patients/${userId}/revert-transfer`); toast.success('Transferência revertida'); fetchPatient(); }} className="text-[10px] text-blue-600 hover:underline" data-testid="revert-transfer-btn">Reverter</button>
          </div>
        )}
        {/* Quick actions */}
        <div className="flex flex-wrap gap-2 mt-3" data-testid="patient-header-actions">
          <button onClick={() => setActiveTab('plano')} className="text-[10px] bg-brand-green/5 text-brand-green px-3 py-1.5 rounded-full hover:bg-brand-green/10">Criar plano</button>
          <button onClick={() => setActiveTab('consultas')} className="text-[10px] bg-brand-green/5 text-brand-green px-3 py-1.5 rounded-full hover:bg-brand-green/10">Marcar consulta</button>
          <button onClick={() => setActiveTab('acompanhamento')} className="text-[10px] bg-brand-green/5 text-brand-green px-3 py-1.5 rounded-full hover:bg-brand-green/10">Ver diário</button>
          <button onClick={async () => {
            const nutris = await axios.get(`${API}/api/staff`);
            const others = (nutris.data || []).filter(n => n.user_id !== patient.assigned_nutritionist);
            if (others.length === 0) { toast.error('Sem outras nutricionistas disponíveis'); return; }
            const target = others[0];
            if (window.confirm(`Transferir para ${target.name}?`)) {
              await axios.put(`${API}/api/patients/${userId}/transfer`, { target_nutri_id: target.user_id });
              toast.success(`Transferida para ${target.name}`); fetchPatient();
            }
          }} className="text-[10px] bg-blue-50 text-blue-600 px-3 py-1.5 rounded-full hover:bg-blue-100" data-testid="transfer-patient-btn">Transferir</button>
          <div className="ml-auto flex flex-wrap gap-1">
            {patient.status === 'arquivado' && (
              <button onClick={async () => { await axios.put(`${API}/api/patients/${userId}/status`, { status: 'ativo' }); toast.success('Paciente reativada'); fetchPatient(); }} className="text-[10px] text-brand-green border border-brand-green/30 px-3 py-1.5 rounded-full hover:bg-brand-green/5" data-testid="reactivate-patient-btn">Reativar</button>
            )}
            {patient.status !== 'arquivado' && (
              <button onClick={async () => { if (!window.confirm('Arquivar ' + (patient.name || '') + '? Os dados ficam preservados e pode ser reativada.')) return; await axios.put(`${API}/api/patients/${userId}/status`, { status: 'arquivado' }); toast.success('Paciente arquivada'); fetchPatient(); }} className="text-[10px] text-brand-muted border border-brand-border px-3 py-1.5 rounded-full hover:bg-brand-bg" data-testid="archive-patient-btn">Arquivar</button>
            )}
            <button onClick={async () => {
              const ok = await confirmDelete({
                description: `Vai apagar permanentemente a paciente "${patient.name || ''}" e TODOS os seus dados (consultas, pagamentos, planos alimentares, análises, mensagens, ficheiros). Esta ação é irreversível e não pode ser desfeita.`,
                linked: ['consultas', 'pagamentos', 'planos alimentares', 'análises', 'mensagens', 'ficheiros do Drive'],
              });
              if (!ok) return;
              try { await axios.delete(`${API}/api/patients/${userId}/permanent`); toast.success('Paciente eliminada permanentemente'); navigate('/admin/pacientes'); } catch(e) { toast.error(e.response?.data?.detail || 'Erro'); }
            }} className="text-[10px] text-red-500 border border-red-200 px-3 py-1.5 rounded-full hover:bg-red-50" data-testid="delete-patient-btn">Eliminar</button>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" ref={tabsWrapperRef}>
        <div className="flex items-center gap-2 mb-6">
          <TabsList ref={tabsListRef} className="flex-1 overflow-x-auto flex justify-start gap-0 bg-white border border-brand-border rounded-2xl p-1" style={{ overflowAnchor: 'none' }} data-testid="patient-tabs">
            {(() => {
              const order = tabOrder.length > 0 ? tabOrder : NEW_TAB_KEYS;
              const sorted = [...NEW_TABS].sort((a, b) => {
                const ia = order.indexOf(a.v); const ib = order.indexOf(b.v);
                return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
              });
              return sorted.map(t => (
                <TabsTrigger key={t.v} value={t.v} className="flex-shrink-0 text-xs px-3 py-2 rounded-xl data-[state=active]:bg-brand-green data-[state=active]:text-white" data-testid={`tab-trigger-${t.v}`}>
                  {t.l}
                </TabsTrigger>
              ));
            })()}
          </TabsList>
          <button onClick={() => { const order = tabOrder.length > 0 ? tabOrder.filter(t => NEW_TAB_KEYS.includes(t)) : NEW_TAB_KEYS; const missing = NEW_TAB_KEYS.filter(t => !order.includes(t)); setTempOrder([...order, ...missing]); setShowTabOrder(true); }} className="text-brand-placeholder hover:text-brand-green p-1 flex-shrink-0" title="Organizar tabs" data-testid="reorder-tabs-btn">
            <ArrowsDownUp size={16} />
          </button>
        </div>
        <TabsContent value="resumo"><ResumoTab section="resumo" patient={patient} onRefresh={fetchPatient} onNavigateTab={setActiveTab} /></TabsContent>
        <TabsContent value="consultas" className="space-y-5">
          <ResumoTab section="consultas" patient={patient} onRefresh={fetchPatient} onNavigateTab={setActiveTab} />
          <ConsultasTab patientId={userId} onOpenNewProgram={openNewProgram} />
        </TabsContent>
        <TabsContent value="clinico" className="space-y-5">
          <AnamneseTab patientId={userId} />
          <AnálisesTab patientId={userId} />
          <ResumoTab section="clinico" patient={patient} onRefresh={fetchPatient} onNavigateTab={setActiveTab} />
          <NecessidadesTab patientId={userId} patient={patient} />
        </TabsContent>
        <TabsContent value="evolucao" className="space-y-5">
          <EvolucaoUnifiedTab patientId={userId} patient={patient} />
          <PerimetrosTab patientId={userId} />
          <GlicemiaTab patientId={userId} />
          <ClinicoTab patientId={userId} />
          <HidratacaoTab patientId={userId} />
          <PassosTab patientId={userId} />
        </TabsContent>
        <TabsContent value="plano" className="space-y-5">
          <PlanosTab patientId={userId} />
          <RecipesTab patientId={userId} />
          <MetasAdminTab patientId={userId} />
          {isBabyPatient(patient) && <BabyMenuTab patientId={userId} />}
          {isBabyPatient(patient) && <FoodIntroductionsTab patientId={userId} />}
        </TabsContent>
        <TabsContent value="acompanhamento" className="space-y-5">
          <DiárioTab patientId={userId} />
          <CheckinsTab patientId={userId} patient={patient} />
          <PatientChallengesHistory patientId={userId} />
          <MensagensProgramadasTab patientId={userId} />
          <NotificationPreferencesPanel patientId={userId} mode="admin" />
        </TabsContent>
        <TabsContent value="documentos" className="space-y-5">
          <ResumoTab section="documentos" patient={patient} onRefresh={fetchPatient} onNavigateTab={setActiveTab} />
          <DriveTab patientId={userId} />
        </TabsContent>
        <TabsContent value="dados" className="space-y-5">
          <ResumoTab section="dados" patient={patient} onRefresh={fetchPatient} onNavigateTab={setActiveTab} />
          <PagamentosPatientTab patientId={userId} />
        </TabsContent>
      </Tabs>
      {/* Tab reorder dialog */}
      <Dialog open={showTabOrder} onOpenChange={v => { if (!v) setShowTabOrder(false); }}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader>
            <DialogTitle className="font-heading text-brand-text">Organizar tabs</DialogTitle>
            <DialogDescription className="sr-only">Reordenar a sequência dos separadores da ficha da paciente</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 max-h-[50vh] overflow-y-auto">
            {tempOrder.map((v, i) => (
              <div key={v} className="flex items-center gap-2 bg-brand-bg/30 rounded-xl px-3 py-2">
                <span className="text-xs text-brand-text flex-1">{NEW_TAB_LABELS[v] || v}</span>
                <button onClick={() => moveTab(i, -1)} disabled={i === 0} className="text-brand-placeholder hover:text-brand-green disabled:opacity-30 p-0.5"><CaretUp size={13} /></button>
                <button onClick={() => moveTab(i, 1)} disabled={i === tempOrder.length - 1} className="text-brand-placeholder hover:text-brand-green disabled:opacity-30 p-0.5"><CaretDown size={13} /></button>
              </div>
            ))}
          </div>
          <button onClick={() => { saveTabOrder(tempOrder); setShowTabOrder(false); toast.success('Ordem guardada'); }} className="w-full bg-brand-green text-white rounded-full py-3 font-medium mt-3">Guardar ordem</button>
        </DialogContent>
      </Dialog>
      {/* Program editor */}
      <Dialog open={showProgramForm} onOpenChange={setShowProgramForm}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader>
            <DialogTitle className="font-heading text-brand-text">Estado do programa</DialogTitle>
            <DialogDescription className="sr-only">Editar a duração, valor e datas do programa desta paciente</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-brand-muted mb-1 block">Duração</Label>
                <select value={progForm.duration || ''} onChange={e => setProgForm({ ...progForm, duration: e.target.value })} className="w-full border border-brand-border rounded-xl px-3 py-2 text-sm bg-white" data-testid="program-duration">
                  <option value="">Selecionar...</option>
                  <option value="mensal">Mensal</option>
                  <option value="trimestral">Trimestral</option>
                  <option value="semestral">Semestral</option>
                </select>
              </div>
              <div><Label className="text-xs text-brand-muted mb-1 block">Valor do programa (€)</Label><Input type="number" step="0.01" value={progForm.program_value} onChange={e => setProgForm({ ...progForm, program_value: e.target.value })} className="rounded-xl border-brand-border" placeholder="Ex: 60" data-testid="program-value" /></div>
              <div><Label className="text-xs text-brand-muted mb-1 block">Total de consultas</Label><Input type="number" value={progForm.total_sessions} onChange={e => setProgForm({ ...progForm, total_sessions: parseInt(e.target.value) || '' })} className="rounded-xl border-brand-border" data-testid="program-total" /></div>
              <div><Label className="text-xs text-brand-muted mb-1 block">Consultas realizadas</Label><Input type="number" value={progForm.completed_sessions} onChange={e => setProgForm({ ...progForm, completed_sessions: parseInt(e.target.value) || '' })} className="rounded-xl border-brand-border" data-testid="program-completed" /></div>
              <div><Label className="text-xs text-brand-muted mb-1 block">Data de início</Label><Input type="date" value={progForm.start_date || ''} onChange={e => setProgForm({ ...progForm, start_date: e.target.value })} className="rounded-xl border-brand-border" /></div>
              <div><Label className="text-xs text-brand-muted mb-1 block">Data prevista de fim</Label><Input type="date" value={progForm.end_date || ''} onChange={e => setProgForm({ ...progForm, end_date: e.target.value })} className="rounded-xl border-brand-border" /></div>
            </div>
            <button onClick={saveProgram} className="w-full bg-brand-green text-white rounded-full py-3 font-medium">Guardar</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
